<template>
  <div class="sales-shell">
    <!-- HERO -->
    <section class="sales-hero">
      <div>
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Tonight's Sales</h1>
        <p class="hero-copy">
          Pick the five pours for the night, manage plates, and ring sales fast.
        </p>
      </div>

      <div class="hero-date">
        <span>Service Date</span>
        <input
          type="date"
          v-model="serviceDate"
          @change="handleServiceDateChange"
        />
      </div>
    </section>

    <!-- NIGHT SETUP -->
    <section class="night-panel">
      <div class="night-prices">
        <label>
          <span>Glass Price</span>
          <input
            type="number"
            v-model.number="glassPrice"
            min="0"
            step="0.01"
            placeholder="12.00"
          />
        </label>
        

        <button
          type="button"
          class="ghost-action plate-menu-button"
          @click="openPlateMenuSetupPopup"
        >
          Plate Menu
        </button>
      </div>

      <div class="pour-grid">
        <article
          v-for="slot in pourSlots"
          :key="slot.key"
          class="pour-card"
        >
          <div class="pour-card-top">
            <div>
              <p class="label">{{ slot.label }}</p>
              <h2>{{ nightlyPours[slot.key].item_name || 'Choose Wine' }}</h2>
            </div>

            <span class="mini-pill">
              ${{ money(glassPrice) }}
            </span>
          </div>

          <select
            v-model="nightlyPours[slot.key].item_no"
            @change="selectPourWine(slot.key)"
          >
            <option value="">Select {{ slot.label }}</option>
            <option
              v-for="item in eligibleWinesForSlot(slot.key)"
              :key="`${slot.key}-${item.item_no}`"
              :value="item.item_no"
            >
              {{ item.item_name }} — {{ item.quantity_on_hand ?? 0 }} on hand
            </option>
          </select>

          <div class="pour-card-actions">
            <button
              class="primary-action"
              @click="addGlass(slot.key)"
              :disabled="!nightlyPours[slot.key].item_no"
            >
              + Glass
            </button>

            <button
              class="ghost-action"
              @click="addHalfGlass(slot.key)"
              :disabled="!nightlyPours[slot.key].item_no"
            >
              + Half Glass
            </button>

            <button
              class="ghost-action"
              @click="clearPourSlot(slot.key)"
            >
              Sub Out
            </button>
          </div>
        </article>
      </div>
    </section>


    <!-- OPEN TABLES -->
    <section class="open-tables-panel">
      <div class="open-tables-header">
        <div>
          <p class="eyebrow">Restaurant Service</p>
          <h2>Open Tables</h2>
        </div>

        <button type="button" class="primary-action" @click="createTableTicket">
          + New Table
        </button>
      </div>

      <div v-if="openTableTickets.length" class="open-table-list">
        <button
          v-for="ticket in openTableTickets"
          :key="ticket.ticket_id"
          type="button"
          class="open-table-card"
          :class="{ active: Number(currentTableTicketId) === Number(ticket.ticket_id) }"
          @click="openTableTicket(ticket.ticket_id)"
        >
          <strong>{{ ticket.table_name }}</strong>
          <span>{{ ticket.line_count }} item{{ Number(ticket.line_count) === 1 ? '' : 's' }}</span>
          <b>${{ money(ticket.total_sale) }}</b>
        </button>
      </div>

      <div v-else class="empty-open-tables">
        <p>No open tables yet.</p>
      </div>

      <div v-if="activeTableTicket" class="active-table-banner">
        <span>
          Active table:
          <strong>{{ activeTableTicket.table_name }}</strong>
        </span>

        <button type="button" class="ghost-action small" @click="saveCurrentTicketToTable" :disabled="!currentSale.items.length">
          Save Table
        </button>

        <button type="button" class="cancel-action small" @click="closeActiveTableView">
          Leave Table
        </button>
      </div>
    </section>

    <!-- SELLING AREA -->
    <section class="register-grid">
      <!-- FAST BUTTONS -->
      <article class="register-card">
        <div class="section-heading">
          <p class="eyebrow">Fast Ring</p>
          <h2>Add to Ticket</h2>
        </div>

        <div class="fast-buttons">
          <template
            v-for="slot in pourSlots"
            :key="`quick-group-${slot.key}`"
          >
            <button
              class="fast-sale-button"
              :disabled="!nightlyPours[slot.key].item_no"
              @click="addGlass(slot.key)"
            >
              <span>{{ slot.label }}</span>
              <strong>+ Glass</strong>
            </button>

            <button
              class="fast-sale-button half-glass-button"
              :disabled="!nightlyPours[slot.key].item_no"
              @click="addHalfGlass(slot.key)"
            >
              <span>{{ slot.label }}</span>
              <strong>+ Half Glass</strong>
            </button>
          </template>

          <button
            class="fast-sale-button plate-button"
            @click="addPlateEnglish"
          >
            <span>Food</span>
            <strong>+ English</strong>
          </button>

          <button
            class="fast-sale-button plate-button"
            @click="addPlateInterestingWoman"
          >
            <span>Food</span>
            <strong>+ Interesting Woman</strong>
          </button>

          <button
            class="fast-sale-button plate-button"
            @click="addPlateTogetherly"
          >
            <span>Food</span>
            <strong>+ Togetherly</strong>
          </button>

          <button
            type="button"
            class="fast-sale-button retail-button"
            @click="addMycoPollenToTicket"
          >
            <span>Retail</span>
            <strong>+ Myco Pollen</strong>
          </button>

          <button
            type="button"
            class="fast-sale-button retail-button"
            @click="addMushroomOatMilkToTicket"
          >
            <span>Retail</span>
            <strong>+ Mushroom Oat Milk</strong>
          </button>

          <button
            type="button"
            class="fast-sale-button retail-button"
            @click="addFizzyWaterToTicket"
          >
            <span>Retail</span>
            <strong>+ Fizzy Water</strong>
          </button>

          <button
            type="button"
            class="fast-sale-button retail-button"
            @click="addTeeShirtToTicket"
          >
            <span>Merch</span>
            <strong>+ Tee Shirt</strong>
          </button>

          <button
            type="button"
            class="fast-sale-button misc-sale-button"
            @click="openMiscSalePopup"
          >
            <span>Flexible</span>
            <strong>+ Misc Sale</strong>
          </button>

          <button
            type="button"
            class="fast-sale-button wine-tour-button"
            @click="openWineTourModal"
          >
            <span>Experience</span>
            <strong>+ Wine Tour</strong>
          </button>
        </div>

        <div class="bottle-panel">
          <div class="bottle-panel-header">
            <div>
              <p class="eyebrow">Bottle Sales</p>
              <h3>Full Bottle</h3>
            </div>

            <button
              type="button"
              class="ghost-action small"
              @click="openReserveBottlePopup"
            >
              Reserve Bottle
            </button>
          </div>

          <label>
            <span>Bottle</span>
            <select
              v-model="bottleEntry.item_no"
              @change="selectBottleWine"
            >
              <option value="">Choose bottle from inventory</option>
              <option
                v-for="item in regularBottleInventory"
                :key="`bottle-${item.item_no}`"
                :value="item.item_no"
              >
                {{ item.item_name }} — {{ item.quantity_on_hand ?? 0 }} on hand
              </option>
            </select>
          </label>

          <div class="bottle-entry-row">
            <label>
              <span>Qty</span>
              <input
                type="number"
                v-model.number="bottleEntry.quantity"
                min="1"
                step="1"
              />
            </label>

            <label>
              <span>Price</span>
              <input
                type="number"
                v-model.number="regularBottlePrice"
                min="0.01"
                step="0.01"
              />
            </label>
          </div>

          <button
            type="button"
            class="primary-action bottle-add-button"
            @click="addBottleToTicket('BOTTLE')"
            :disabled="!bottleEntry.item_no"
          >
            + Add Bottle
          </button>
        </div>
      </article>

      <!-- CURRENT TICKET -->
      <article class="register-card ticket-card">
        <div class="ticket-header">
          <div>
            <p class="eyebrow">{{ currentTableLabel }}</p>
            <h2>${{ money(cartTotal) }}</h2>
          </div>

          <span class="status-pill">
            {{ currentSale.items.length }} line{{ currentSale.items.length === 1 ? '' : 's' }}
          </span>
        </div>

        <div v-if="currentSale.items.length" class="ticket-list">
          <template
            v-for="(line, index) in currentSale.items"
            :key="`${line.item_name}-${line.sale_mode}-${index}`"
          >
            <div
              v-if="!isHiddenWineTourComponent(line)"
              class="ticket-line"
            >
              <div>
                <h3>{{ line.item_name }}</h3>
                <p>
                  {{ displaySaleMode(line.sale_mode) }}
                  <span v-if="line.inventory_quantity_used">
                    · uses {{ line.inventory_quantity_used }} inventory
                  </span>
                </p>
              </div>

              <div class="ticket-line-price">
                <strong>${{ money(line.total_revenue) }}</strong>
                <span>Qty {{ line.quantity_sold }}</span>
              </div>

              <div class="line-actions">
                <button
                  v-if="line.sale_mode !== 'WINE_TOUR'"
                  type="button"
                  @click="incrementLine(index)"
                >
                  +
                </button>

                <button type="button" @click="decrementLine(index)">−</button>
              </div>
            </div>
          </template>
        </div>

        <div v-else class="empty-ticket">
          <h3>No items yet</h3>
          <p>Add glasses or food from the buttons on the left.</p>
        </div>

        <label class="ticket-note">
          <span>Notes</span>
          <input
            type="text"
            v-model="currentSale.notes"
            placeholder="Optional note"
          />
        </label>

        <div class="checkout-actions">
          <button
            class="cancel-action"
            @click="clearCurrentSale"
            :disabled="!currentSale.items.length"
          >
            Clear
          </button>

          <button
            class="ghost-action"
            @click="saveCurrentTicketToTable"
            :disabled="!currentSale.items.length"
          >
            Save Table
          </button>

          <button
            class="cash-action"
            @click="openCashTenderPopup"
            :disabled="!currentSale.items.length"
          >
            Cash
          </button>

          <button
            class="square-action"
            @click="completeSquareSale"
            :disabled="!currentSale.items.length"
          >
            Square
          </button>
        </div>
      </article>
    </section>

    <!-- SALES HISTORY -->
    <section class="history-panel">
      <div class="history-header">
        <div>
          <p class="eyebrow">Tonight</p>
          <h2>Closed Sales</h2>
        </div>

        <button class="ghost-action" @click="fetchSales">
          Refresh
        </button>
      </div>

      <div v-if="sales.length" class="sales-history-list">
        <article
          v-for="sale in sortedSales"
          :key="sale.sale_id"
          class="history-card"
          :class="{ adjusted: Number(sale.adjustment_count || 0) > 0 }"
        >
          <div class="history-sale-main">
            <div>
              <p class="label">Sale #{{ sale.sale_id }}</p>

              <div class="history-title-row">
                <h3>${{ money(Number(sale.net_sale ?? sale.total_sale)) }}</h3>

                <span
                  v-if="Number(sale.adjustment_count || 0) > 0"
                  class="adjusted-badge"
                >
                  Adjusted · {{ sale.adjustment_count }}
                </span>
              </div>
            </div>

            <div class="sale-total-stack">
              <span>Original</span>
              <strong>${{ money(sale.total_sale) }}</strong>

              <template v-if="Number(sale.adjustment_count || 0) > 0">
                <span>Refunds</span>
                <strong class="refund-text">-${{ money(sale.refund_total) }}</strong>

                <span>Net</span>
                <strong class="net-sale-text">${{ money(sale.net_sale) }}</strong>
              </template>
            </div>
          </div>

          <div class="history-meta">
            <span>{{ sale.payment_method || '—' }}</span>
            <strong>{{ formatDate(sale.sale_date) }}</strong>
          </div>

          <div
            v-if="Number(sale.adjustment_count || 0) > 0"
            class="adjustment-card-summary"
          >
            <span>
              Refunds <strong>${{ money(sale.refund_total) }}</strong>
            </span>

            <span v-if="Number(sale.returned_inventory_qty || 0) > 0">
              Returned <strong>{{ number(sale.returned_inventory_qty) }}</strong>
            </span>

            <span v-if="Number(sale.dumped_faulty_qty || 0) > 0">
              Dumped/Faulty <strong>{{ number(sale.dumped_faulty_qty) }}</strong>
            </span>
          </div>

          <div class="history-actions">
            <button
              class="ghost-action small"
              @click="openAdjustmentPopup(sale)"
            >
              Adjust
            </button>

            <button
              class="ghost-action small"
              :class="{ 'has-adjustments-button': Number(sale.adjustment_count || 0) > 0 }"
              @click="openAdjustmentHistoryPopup(sale)"
            >
              {{ Number(sale.adjustment_count || 0) > 0 ? 'View Adjustments' : 'History' }}
            </button>

            <button
              class="danger-action small"
              @click="deleteSale(sale.sale_id)"
            >
              Delete
            </button>
          </div>
        </article>
      </div>

      <div v-else class="empty-history">
        <p>No sales closed yet for this service date.</p>
      </div>
    </section>

    <!-- WARNING MODAL -->
    <div v-if="showWarning" class="modal-overlay">
      <div class="sales-modal small-modal">
        <div class="modal-topline amber"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Heads Up</p>
            <h2>Check This</h2>
            <p>{{ warningMessage }}</p>
          </div>

          <button type="button" @click="showWarning = false" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <button type="button" @click="showWarning = false" class="primary-action">
            OK
          </button>
        </div>
      </div>
    </div>



    <!-- CASH TENDER MODAL -->
    <div v-if="cashTender.show" class="modal-overlay">
      <div class="sales-modal small-modal">
        <div class="modal-topline gold"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Cash Payment</p>
            <h2>Take Cash</h2>
            <p>
              Enter the cash received and LotLedger will calculate the change due.
            </p>
          </div>

          <button type="button" @click="closeCashTenderPopup" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <section class="cash-tender-card">
            <div>
              <span>Sale Total</span>
              <strong>${{ money(cashTenderTotal) }}</strong>
            </div>

            <label>
              <span>Cash Received</span>
              <input
                type="number"
                v-model.number="cashTender.cash_received"
                min="0"
                step="0.01"
                inputmode="decimal"
                placeholder="0.00"
                @keyup.enter="submitCashTender"
              />
            </label>

            <div>
              <span>Change Due</span>
              <strong :class="{ 'bad-change': Number(cashChangeDue) < 0 }">
                ${{ money(Math.max(cashChangeDue, 0)) }}
              </strong>
            </div>
          </section>

          <footer class="modal-actions cash-tender-actions">
            <button
              type="button"
              class="cancel-action"
              :disabled="cashTender.working"
              @click="closeCashTenderPopup"
            >
              Cancel
            </button>

            <button
              type="button"
              class="cash-action"
              :disabled="cashTender.working || Number(cashChangeDue) < 0 || !currentSale.items.length"
              @click="submitCashTender"
            >
              {{ cashTender.working ? 'Saving...' : 'Complete Cash Sale' }}
            </button>
          </footer>
        </div>
      </div>
    </div>






    <!-- MISC SALE MODAL -->
    <div v-if="showMiscSalePopup" class="modal-overlay">
      <div class="sales-modal small-modal">
        <div class="modal-topline gold"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Misc Sale</p>
            <h2>Custom Item</h2>
            <p>Use this for one-off charges, special items, or anything without inventory.</p>
          </div>

          <button type="button" @click="closeMiscSalePopup" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <section class="reserve-form">
            <label>
              <span>Description</span>
              <input
                type="text"
                v-model="miscSale.description"
                placeholder="Example: Corkage, special snack, event item"
              />
            </label>

            <label>
              <span>Sale Amount</span>
              <input
                type="number"
                v-model.number="miscSale.amount"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </label>

            <label>
              <span>Known Cost Optional</span>
              <input
                type="number"
                v-model.number="miscSale.cost"
                min="0"
                step="0.01"
                placeholder="Leave blank if unknown"
              />
            </label>
          </section>

          <footer class="modal-actions">
            <button type="button" class="cancel-action" @click="closeMiscSalePopup">
              Cancel
            </button>

            <button type="button" class="primary-action" @click="addMiscSaleToTicket">
              Add Misc Sale
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- WINE TOUR MODAL -->
    <div v-if="showWineTourModal" class="modal-overlay">
      <div class="sales-modal wine-tour-modal">
        <div class="modal-topline gold"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Experience Sale</p>
            <h2>Wine Tour</h2>
            <p>
              Add a $30 tour to the current ticket: three 1.5 oz pours and Togetherly.
              Change the wines here for this ticket, or save the wine selections as the new default.
            </p>
          </div>

          <button type="button" @click.prevent.stop="closeWineTourModal" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <div v-if="wineTourLoading" class="empty-ticket">
            <h3>Loading Wine Tour options...</h3>
            <p>Pulling current wines, Togetherly, and defaults.</p>
          </div>

          <template v-else>
            <section class="form-panel wine-tour-form">
              <div class="panel-heading">
                <h3>Tour Setup</h3>
                <p>Wine selections can be changed per ticket. The plate is locked to Togetherly.</p>
              </div>

              <div class="form-grid">
                <label>
                  <span>Participants</span>
                  <input
                    type="number"
                    v-model.number="wineTourForm.quantity"
                    min="1"
                    step="1"
                  />
                </label>

                <label>
                  <span>White Bubbles</span>
                  <select v-model.number="wineTourForm.white_bubbles_item_no">
                    <option :value="null">Choose white bubbles</option>
                    <option
                      v-for="wine in wineTourOptions.wines"
                      :key="`tour-bubbles-${wine.item_no}`"
                      :value="wine.item_no"
                    >
                      {{ wine.item_name }} — {{ number(wine.quantity_on_hand) }} bottles
                    </option>
                  </select>
                </label>

                <label>
                  <span>White Flat</span>
                  <select v-model.number="wineTourForm.white_flat_item_no">
                    <option :value="null">Choose white flat</option>
                    <option
                      v-for="wine in wineTourOptions.wines"
                      :key="`tour-white-${wine.item_no}`"
                      :value="wine.item_no"
                    >
                      {{ wine.item_name }} — {{ number(wine.quantity_on_hand) }} bottles
                    </option>
                  </select>
                </label>

                <label>
                  <span>Red / Bordeaux</span>
                  <select v-model.number="wineTourForm.red_item_no">
                    <option :value="null">Choose red / Bordeaux</option>
                    <option
                      v-for="wine in wineTourOptions.wines"
                      :key="`tour-red-${wine.item_no}`"
                      :value="wine.item_no"
                    >
                      {{ wine.item_name }} — {{ number(wine.quantity_on_hand) }} bottles
                    </option>
                  </select>
                </label>

                <section class="wine-tour-locked-plate">
                  <span>Plate</span>
                  <strong>{{ getWineTourPlateName(wineTourForm.nightly_plate_id) || 'Togetherly' }}</strong>
                  <p>Wine Tour uses Togetherly. Other plates are sold separately.</p>
                </section>
              </div>

              <label class="full-width save-default-row">
                <input
                  type="checkbox"
                  v-model="wineTourForm.save_as_default"
                />
                <span>Save these wine selections as the new Wine Tour default</span>
              </label>
            </section>

            <section class="wine-tour-summary-card">
              <div>
                <span>Tour Price</span>
                <strong>${{ money(wineTourOptions.product?.sale_price || 30) }}</strong>
              </div>

              <div>
                <span>Participants</span>
                <strong>{{ Math.max(1, Number(wineTourForm.quantity || 1)) }}</strong>
              </div>

              <div>
                <span>Total</span>
                <strong>${{ money(getWineTourEstimatedTotal()) }}</strong>
              </div>
            </section>

            <section class="wine-tour-component-preview">
              <p class="label">Selected Components</p>
              <div>White bubbles: <strong>{{ getWineTourWineName(wineTourForm.white_bubbles_item_no) || '—' }}</strong></div>
              <div>White flat: <strong>{{ getWineTourWineName(wineTourForm.white_flat_item_no) || '—' }}</strong></div>
              <div>Red: <strong>{{ getWineTourWineName(wineTourForm.red_item_no) || '—' }}</strong></div>
              <div>Plate: <strong>{{ getWineTourPlateName(wineTourForm.nightly_plate_id) || '—' }}</strong></div>
            </section>

            <footer class="modal-actions">
              <button
                type="button"
                class="cancel-action"
                :disabled="wineTourSubmitting"
                @click.prevent.stop="closeWineTourModal"
              >
                Cancel
              </button>

              <button
                type="button"
                class="primary-action"
                :disabled="wineTourSubmitting"
                @click.prevent.stop="addWineTourToTicket"
              >
                {{ wineTourSubmitting ? 'Adding...' : 'Add to Ticket' }}
              </button>
            </footer>
          </template>
        </div>
      </div>
    </div>

    <!-- PLATE MENU SETUP MODAL -->
    <div v-if="showPlateMenuPopup" class="modal-overlay">
      <div class="sales-modal plate-menu-modal">
        <div class="modal-topline gold"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Tonight’s Food</p>
            <h2>Food Menu Setup</h2>
            <p>
              Build tonight’s food menu for {{ formatDate(serviceDate) }}.
              These recipes will be used when food buttons are sold tonight.
            </p>
          </div>

          <button type="button" @click="showPlateMenuPopup = false" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <section class="food-menu-accordion">
              <article
                v-for="plate in plateMenuDrafts"
                :key="plate.nightly_plate_id"
                class="food-menu-item"
                :class="{ open: Number(expandedFoodMenuItemId) === Number(plate.nightly_plate_id) }"
              >
                <button
                  type="button"
                  class="food-menu-summary"
                  @click="toggleFoodMenuItem(plate.nightly_plate_id)"
                >
                  <div class="food-menu-title">
                    <p class="label">Food Item</p>
                    <h3>{{ plate.plate_name }}</h3>
                    <p>{{ plate.description }}</p>
                  </div>

                  <div class="food-menu-summary-right">
                    <strong>${{ money(plate.price) }}</strong>
                    <span>
                      {{ Number(expandedFoodMenuItemId) === Number(plate.nightly_plate_id) ? '−' : '+' }}
                    </span>
                  </div>
                </button>

                <div
                  v-if="Number(expandedFoodMenuItemId) === Number(plate.nightly_plate_id)"
                  class="food-menu-details"
                >
                  <div class="plate-setup-header">
                    <div>
                      <p class="label">Edit Item</p>
                      <h3>{{ plate.plate_name }}</h3>
                    </div>

                    <label class="plate-price-field">
                      <span>Price</span>
                      <input
                        type="number"
                        v-model.number="plate.price"
                        min="0"
                        step="0.01"
                      />
                    </label>
                  </div>

                  <label class="full-width">
                    <span>Description</span>
                    <input
                      type="text"
                      v-model="plate.description"
                      placeholder="Describe tonight’s food item..."
                    />
                  </label>

                  <div class="plate-ingredient-editor">
                    <div class="ingredient-editor-header">
                      <span>Ingredient</span>
                      <span>Portion</span>
                      <span>Unit</span>
                      <span>On Hand</span>
                      <span>Cost</span>
                    </div>

                    <div
                      v-for="ingredient in plate.ingredients"
                      :key="ingredient.nightly_plate_line_id || ingredient.ingredient_id"
                      class="ingredient-editor-row"
                    >
                      <strong>{{ ingredient.ingredient_name }}</strong>

                      <input
                        type="number"
                        v-model.number="ingredient.serving_quantity"
                        min="0"
                        step="0.01"
                      />

                      <span>{{ ingredient.serving_unit }}</span>

                      <span>{{ number(ingredient.quantity_on_hand) }}</span>

                      <span>
                        ${{ money(Number(ingredient.serving_quantity || 0) * Number(ingredient.ingredient_cost || 0)) }}
                      </span>
                    </div>
                  </div>

                  <div class="plate-estimate-row">
                    <span>Estimated COGS</span>
                    <strong>
                      ${{
                        money(
                          (plate.ingredients || []).reduce((sum, ingredient) => {
                            return sum + Number(ingredient.serving_quantity || 0) * Number(ingredient.ingredient_cost || 0);
                          }, 0)
                        )
                      }}
                    </strong>
                  </div>
                </div>
              </article>
            </section>

          <footer class="modal-actions">
            <button
              type="button"
              class="cancel-action"
              @click="showPlateMenuPopup = false"
            >
              Cancel
            </button>

            <button
              type="button"
              class="primary-action"
              @click="savePlateMenuSetup"
            >
              Save Food Menu
            </button>
          </footer>
        </div>
      </div>
    </div>
        <!-- CREATE NIGHTLY PLATE MENU MODAL -->
    <div v-if="showCreatePlateMenuPopup" class="modal-overlay">
      <div class="sales-modal plate-menu-modal">
        <div class="modal-topline gold"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Food Menu</p>
            <h2>Create Tonight’s Food</h2>
            <p>
              Choose which food recipes are available for {{ formatDate(serviceDate) }}.
            </p>
          </div>

          <button type="button" @click="showCreatePlateMenuPopup = false" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <section class="plate-template-list">
            <article
              v-for="template in plateTemplates"
              :key="template.plate_template_id"
              class="plate-template-card"
              :class="{ selected: selectedPlateTemplateIds.includes(template.plate_template_id) }"
              @click="togglePlateTemplate(template.plate_template_id)"
            >
              <div>
                <p class="label">Template</p>
                <h3>{{ template.plate_name }}</h3>
                <p>{{ template.description }}</p>
              </div>

              <div class="template-check">
                {{ selectedPlateTemplateIds.includes(template.plate_template_id) ? '✓' : '+' }}
              </div>
            </article>
          </section>

          <footer class="modal-actions">
            <button
              type="button"
              class="cancel-action"
              @click="showCreatePlateMenuPopup = false"
            >
              Cancel
            </button>

            <button
              type="button"
              class="primary-action"
              @click="createNightlyPlateMenu"
            >
              Create Food Menu
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- RESERVE BOTTLE MODAL -->
    <div v-if="showReserveBottlePopup" class="modal-overlay">
      <div class="sales-modal small-modal">
        <div class="modal-topline gold"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Reserve Bottle</p>
            <h2>Add Reserve</h2>
            <p>Use this for nicer bottles, special pulls, or table bottles.</p>
          </div>

          <button type="button" @click="showReserveBottlePopup = false" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <section class="reserve-form">
            <label>
              <span>Reserve Bottle</span>
              <select
                v-model="bottleEntry.item_no"
                @change="selectBottleWine"
              >
                <option value="">Choose reserve bottle</option>
                <option
                  v-for="item in reserveBottleInventory"
                  :key="`reserve-${item.item_no}`"
                  :value="item.item_no"
                >
                  {{ item.item_name }} — {{ item.quantity_on_hand ?? 0 }} on hand
                </option>
              </select>
            </label>

            <div class="bottle-entry-row">
              <label>
                <span>Qty</span>
                <input
                  type="number"
                  v-model.number="bottleEntry.quantity"
                  min="1"
                  step="1"
                />
              </label>

              <label>
                <span>Reserve Price</span>
                <input
                  type="number"
                  v-model.number="bottleEntry.unit_price"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </label>
            </div>
          </section>

          <footer class="modal-actions">
            <button type="button" @click="showReserveBottlePopup = false" class="cancel-action">
              Cancel
            </button>

            <button
              type="button"
              @click="addBottleToTicket('RESERVE_BOTTLE')"
              class="primary-action"
              :disabled="!bottleEntry.item_no"
            >
              Add Reserve Bottle
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- SALE ADJUSTMENT MODAL -->
    <div v-if="showAdjustmentPopup" class="modal-overlay">
      <div class="sales-modal adjustment-modal">
        <div class="modal-topline amber"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Refund / Return</p>
            <h2>Adjust Sale #{{ saleToAdjust?.sale_id }}</h2>
            <p>
              Record a refund, return, replacement, or fault without wrecking the original sale history.
            </p>
          </div>

          <button type="button" @click="closeAdjustmentPopup" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <section class="adjustment-summary">
            <div>
              <span>Sale Total</span>
              <strong>${{ money(saleToAdjust?.total_sale) }}</strong>
            </div>

            <div>
              <span>Payment</span>
              <strong>{{ saleToAdjust?.payment_method || '—' }}</strong>
            </div>

            <div>
              <span>Date</span>
              <strong>{{ formatDate(saleToAdjust?.sale_date) }}</strong>
            </div>
          </section>

          <section class="form-panel adjustment-form">
            <div class="panel-heading">
              <h3>Adjustment Details</h3>
              <p>Choose whether this affects money, inventory, or both.</p>
            </div>

            <div class="form-grid">
              <label>
                <span>Sale Line</span>
                <select
                  v-model="adjustmentData.sale_line_id"
                  @change="handleAdjustmentLineChange"
                >
                  <option :value="null">General sale adjustment</option>
                  <option
                    v-for="line in saleLinesForAdjustment"
                    :key="line.sale_line_id"
                    :value="line.sale_line_id"
                  >
                    {{ line.item_name }} · {{ displaySaleMode(line.sale_mode) }} · Qty {{ line.quantity_sold || line.quantity }}
                  </option>
                </select>
              </label>

              <label>
                <span>Adjustment Type</span>
                <select v-model="adjustmentData.adjustment_type">
                  <option value="refund">Refund</option>
                  <option value="return">Return</option>
                  <option value="replacement">Replacement</option>
                  <option value="writeoff">Writeoff</option>
                </select>
              </label>

              <label>
                <span>Reason</span>
                <select v-model="adjustmentData.reason">
                  <option value="">Select reason</option>
                  <option
                    v-for="reason in adjustmentReasonOptions"
                    :key="reason"
                    :value="reason"
                  >
                    {{ reason }}
                  </option>
                </select>
              </label>

              <label>
                <span>Refund Amount</span>
                <input
                  type="number"
                  v-model.number="adjustmentData.refund_amount"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </label>

              <label>
                <span>Quantity Affected</span>
                <input
                  type="number"
                  v-model.number="adjustmentData.quantity"
                  min="0"
                  step="0.0001"
                  placeholder="0"
                />
              </label>

              <label>
                <span>Inventory Action</span>
                <select
                  v-model="adjustmentData.inventory_action"
                  :disabled="adjustingPlateLine"
                >
                  <option value="none">No Inventory Impact</option>
                  <option value="return_to_stock">Return to Stock</option>
                  <option value="dumped_faulty">Dumped / Faulty / Spoiled</option>
                </select>
              </label>

              <p v-if="adjustingPlateLine" class="field-note">
                Food items are not returned to inventory. Use the refund amount and reason to record the credit.
              </p>

              <label v-if="adjustmentData.inventory_action !== 'none'">
                <span>Inventory Quantity</span>
                <input
                  type="number"
                  v-model.number="adjustmentData.inventory_quantity"
                  min="0"
                  step="0.0001"
                  placeholder="0"
                />
              </label>

              <label class="full-width">
                <span>Notes</span>
                <input
                  type="text"
                  v-model="adjustmentData.notes"
                  placeholder="Optional note..."
                />
              </label>
            </div>
          </section>

          <section class="adjustment-helper">
            <h3>{{ adjustmentHelperTitle }}</h3>
            <p>{{ adjustmentHelperText }}</p>
          </section>

          <footer class="modal-actions">
            <button type="button" @click="closeAdjustmentPopup" class="cancel-action">
              Cancel
            </button>

            <button type="button" @click="submitSaleAdjustment" class="primary-action">
              Save Adjustment
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- SQUARE COMPLETION CONFIRMATION MODAL -->
    <div v-if="showSquareCompletionPopup" class="modal-overlay">
      <div class="sales-modal small-modal">
        <div class="modal-topline gold"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Square Payment</p>
            <h2>Square Completed?</h2>
            <p>
              Confirm whether the Square payment finished successfully.
              This will refresh LotLedger and move the sale into history if Square marked it paid.
            </p>
          </div>

          <button type="button" @click="closeSquareCompletionPopup" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <section class="square-confirm-card">
            <span>Pending Sale</span>
            <strong>#{{ squareCompletionSaleId || pendingSquareSaleId || '—' }}</strong>
            <p>
              After the Square app says the sale is complete, return here and tap Yes.
            </p>
          </section>

          <footer class="modal-actions square-confirm-actions">
            <button
              type="button"
              class="cancel-action"
              :disabled="squareCompletionWorking"
              @click.prevent.stop="handleSquareCompletedNo"
            >
              No
            </button>

            <button
              type="button"
              class="primary-action"
              :disabled="squareCompletionWorking"
              @click.prevent.stop="handleSquareCompletedYes"
            >
              {{ squareCompletionWorking ? 'Refreshing...' : 'Yes, Square Completed' }}
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- SALE ADJUSTMENT HISTORY MODAL -->
    <div v-if="showAdjustmentHistoryPopup" class="modal-overlay">
      <div class="sales-modal adjustment-modal">
        <div class="modal-topline gold"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Adjustment History</p>
            <h2>Sale #{{ saleToAdjust?.sale_id }}</h2>
            <p>Refunds, returns, replacements, and inventory actions recorded against this sale.</p>
          </div>

          <button type="button" @click="showAdjustmentHistoryPopup = false" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <div v-if="saleAdjustments.length" class="adjustment-history-list">
            <article
              v-for="adjustment in saleAdjustments"
              :key="adjustment.adjustment_id"
              class="adjustment-history-card"
            >
              <div>
                <p class="label">{{ formatAdjustmentType(adjustment.adjustment_type) }}</p>
                <h3>{{ adjustment.reason || 'No reason entered' }}</h3>
                <p>
                  {{ adjustment.item_name || 'General sale adjustment' }}
                  · {{ formatInventoryAction(adjustment.inventory_action) }}
                </p>
              </div>

              <div class="adjustment-history-money">
                <strong>${{ money(adjustment.refund_amount) }}</strong>
                <span>{{ formatDate(adjustment.created_at) }}</span>
              </div>
            </article>
          </div>

          <div v-else class="empty-ticket">
            <h3>No adjustments yet</h3>
            <p>This sale has not had refunds, returns, or inventory actions recorded.</p>
          </div>

          <footer class="modal-actions">
            <button
              type="button"
              @click="showAdjustmentHistoryPopup = false"
              class="primary-action"
            >
              Close
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- DELETE MODAL -->
    <div v-if="showDeletePopup" class="modal-overlay">
      <div class="sales-modal small-modal">
        <div class="modal-topline red"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Delete Sale</p>
            <h2>Delete this sale?</h2>
            <p>This should reverse the sale on the backend.</p>
          </div>

          <button type="button" @click="showDeletePopup = false" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body delete-actions">
          <button type="button" @click="showDeletePopup = false" class="cancel-action">
            Cancel
          </button>

          <button type="button" @click="deleteSaleConfirmed" class="danger-action">
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

const SERVINGS_PER_BOTTLE = 4;
// Wine categories used for the five nightly pours
const NIGHTLY_POUR_CATEGORY_IDS = Object.freeze({
  red: 5000,
  white: 5001,
  rose: 5002,
  whiteBubbles: 5007,
  roseBubbles: 5006
});

export default {
  name: 'WineBarSalesPage',

  data() {
    return {
      serviceDate: new Date().toISOString().split('T')[0],

      glassPrice: Number(localStorage.getItem('winebar_glass_price') || 12),      
      squareCheckoutUrl: localStorage.getItem('winebar_square_url') || '',

      availableWineInventory: [],
      sellableRetailItems: [],

      pourSlots: [
        { key: 'red', label: 'Red' },
        { key: 'white', label: 'White' },
        { key: 'rose', label: 'Rosé' },
        { key: 'whiteBubbles', label: 'White Bubbles' },
        { key: 'roseBubbles', label: 'Rosé Bubbles' }
      ],

      nightlyPours: {
        red: this.emptyPourWine(),
        white: this.emptyPourWine(),
        rose: this.emptyPourWine(),
        whiteBubbles: this.emptyPourWine(),
        roseBubbles: this.emptyPourWine()
      },

      currentSale: this.emptySale(),
      openTableTickets: [],
      activeTableTicket: null,
      currentTableTicketId: null,
      cashTender: {
        show: false,
        cash_received: null,
        working: false
      },

      showMiscSalePopup: false,
      miscSale: {
        description: '',
        amount: null,
        cost: null
      },

      showReserveBottlePopup: false,
      regularBottlePrice: 47,

      bottleEntry: {
        item_no: "",
        item_name: "",
        description: "",
        quantity: 1,
        unit_price: 0,
        sale_mode: "BOTTLE"
      },

      showAdjustmentPopup: false,
      showAdjustmentHistoryPopup: false,

      saleToAdjust: null,
      saleLinesForAdjustment: [],
      saleAdjustments: [],

      adjustmentData: {
        sale_id: null,
        sale_line_id: null,
        item_no: null,
        adjustment_type: 'refund',
        reason: '',
        refund_amount: 0,
        quantity: 0,
        inventory_action: 'none',
        inventory_quantity: 0,
        notes: ''
      },

      adjustmentReasonOptions: [
        'Faulted wine',
        'Customer rejected glass',
        'Wrong item',
        'Unopened bottle returned',
        'Opened bottle dumped',
        'Comp replacement',
        'Server mistake',
        'Other'
      ],

      showPlateMenuPopup: false,
      nightlyPlates: [],
      selectedPlate: null,
      plateQuantity: 1,

      plateTemplates: [],
      showCreatePlateMenuPopup: false,
      selectedPlateTemplateIds: [],
      plateMenuDrafts: [],
      expandedFoodMenuItemId: null,

      sales: [],

      showWineTourModal: false,
      wineTourLoading: false,
      wineTourSubmitting: false,
      wineTourOptions: {
        product: null,
        wines: [],
        plates: [],
        defaults: []
      },
      wineTourForm: {
        quantity: 1,
        white_bubbles_item_no: null,
        white_flat_item_no: null,
        red_item_no: null,
        nightly_plate_id: null,
        plate_template_id: null,
        save_as_default: false
      },

      pendingSquareCheckInterval: null,

      showWarning: false,
      warningMessage: '',

      showSquareCompletionPopup: false,
      squareCompletionSaleId: null,
      handlingSquareReturn: false,
      squareCompletionWorking: false,

      showDeletePopup: false,
      saleToDelete: null
    };
  },

  computed: {
    cartTotal() {
      return this.currentSale.items.reduce((sum, line) => {
        return sum + Number(line.total_revenue || 0);
      }, 0);
    },

    currentTableLabel() {
      return this.activeTableTicket?.table_name
        ? `Current Ticket · ${this.activeTableTicket.table_name}`
        : 'Current Ticket';
    },

    cashTenderTotal() {
      return Number(this.cartTotal || 0);
    },

    cashChangeDue() {
      return Number(this.cashTender.cash_received || 0) - Number(this.cashTenderTotal || 0);
    },

    pourEligibleInventory() {
      return this.availableWineInventory.filter(item => {
        return String(item.service_class || '').toLowerCase() !== 'reserve';
      });
    },

    regularBottleInventory() {
      return this.availableWineInventory.filter(item => {
        return String(item.service_class || '').toLowerCase() !== 'reserve';
      });
    },

    reserveBottleInventory() {
      return this.availableWineInventory.filter(item => {
        return String(item.service_class || '').toLowerCase() === 'reserve';
      });
    },
   
    sortedSales() {
      return this.sales.slice().sort((a, b) => Number(b.sale_id) - Number(a.sale_id));
    },

    pendingSquareSaleId() {
      return localStorage.getItem('winebar_pending_square_sale_id');
    },

    selectedAdjustmentLine() {
      return this.saleLinesForAdjustment.find(line => {
        return Number(line.sale_line_id) === Number(this.adjustmentData.sale_line_id);
      }) || null;
    },

    adjustingPlateLine() {
      return this.selectedAdjustmentLine?.sale_mode === 'PLATE';
    },

    adjustmentHelperTitle() {
      if (this.adjustmentData.inventory_action === 'return_to_stock') {
        return 'Return to stock';
      }

      if (this.adjustmentData.inventory_action === 'dumped_faulty') {
        return 'Dumped / faulty';
      }

      return 'No inventory impact';
    },

    adjustmentHelperText() {
      if (this.adjustmentData.inventory_action === 'return_to_stock') {
        return 'Use this only when the item physically goes back into sellable inventory, like an unopened full bottle.';
      }

      if (this.adjustmentData.inventory_action === 'dumped_faulty') {
        return 'Use this for corked, oxidized, rejected, spilled, or dumped wine. Inventory does not come back, but the event is recorded.';
      }

      return 'Use this for money-only adjustments, replacements, customer service credits, or cases where inventory should stay exactly as-is.';
    }
  },

  mounted() {
    this.loadNightlyPours();
    this.fetchAvailableWineInventory();
    this.fetchSellableRetailItems();
    this.fetchSales();
    this.fetchOpenTableTickets();
    this.handleSquareReturn();
    this.fetchNightlyPlates();
    this.fetchPlateTemplates();

    window.addEventListener('focus', this.checkPendingSquareSale);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.startPendingSquarePolling();
    this.checkPendingSquareSale();

    const pendingSquareSaleId = localStorage.getItem('winebar_pending_square_sale_id');

    if (pendingSquareSaleId) {
      this.squareCompletionSaleId = pendingSquareSaleId;
      this.showSquareCompletionPopup = true;
    }
  },

  watch: {
    glassPrice(value) {
      localStorage.setItem('winebar_glass_price', Number(value || 0));
    },
   

    nightlyPours: {
      deep: true,
      handler() {
        localStorage.setItem('winebar_nightly_pours', JSON.stringify(this.nightlyPours));
      }
    }
  },

  beforeUnmount() {
    window.removeEventListener('focus', this.checkPendingSquareSale);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    if (this.pendingSquareCheckInterval) {
      clearInterval(this.pendingSquareCheckInterval);
      this.pendingSquareCheckInterval = null;
    }
  },

  methods: {

  eligibleWinesForSlot(slotKey) {
    const categoryId = NIGHTLY_POUR_CATEGORY_IDS[slotKey];

    if (!categoryId) return [];

    return this.pourEligibleInventory.filter(item =>
      Number(item.category_id) === categoryId
    );
  },

  reconcileNightlyPourSelections() {
    // Remove saved selections that no longer belong
    // to their slot or are no longer available.
    for (const slot of this.pourSlots) {
      const selectedId = this.nightlyPours[slot.key]?.item_no;

      if (!selectedId) continue;

      const valid = this.eligibleWinesForSlot(slot.key).some(item =>
        String(item.item_no) === String(selectedId)
      );

      if (!valid) {
        this.nightlyPours[slot.key] = this.emptyPourWine();
      }
    }
  },

  emptyPourWine() {
      return {
        item_no: '',
        item_name: '',
        description: '',
        servings_per_bottle: SERVINGS_PER_BOTTLE
      };
    },

    emptySale() {
      return {
        sale_date: this.serviceDate,
        payment_method: '',
        notes: '',
        items: []
      };
    },

    handleVisibilityChange() {
      if (!document.hidden) {
        this.checkPendingSquareSale();
      }
    },

    startPendingSquarePolling() {
      if (this.pendingSquareCheckInterval) {
        clearInterval(this.pendingSquareCheckInterval);
      }

      this.pendingSquareCheckInterval = setInterval(() => {
        const pendingSaleId =
          this.squareCompletionSaleId ||
          localStorage.getItem('winebar_pending_square_sale_id');

        if (pendingSaleId) {
          this.checkPendingSquareSale(false);
        }
      }, 10000);
    },

    emptyBottleEntry() {
      return {
        item_no: "",
        item_name: "",
        description: "",
        quantity: 1,
        unit_price: 0,
        sale_mode: "BOTTLE"
      };
    },

    number(value) {
      return Number(value || 0).toLocaleString('en-US', {
        maximumFractionDigits: 4
      });
    },

    handleServiceDateChange() {
      this.currentSale.sale_date = this.serviceDate;
      this.fetchSales();
      this.fetchOpenTableTickets();
      this.fetchNightlyPlates();
    },

    normalizeTableTicketLine(line) {
      return {
        ticket_line_id: line.ticket_line_id || null,
        item_no: line.item_no || null,
        item_name: line.item_name || line.description || 'Item',
        description: line.description || '',
        sale_mode: line.sale_mode || 'MIXED',
        quantity: Number(line.quantity || line.quantity_sold || 1),
        quantity_sold: Number(line.quantity_sold || line.quantity || 1),
        unit_price: Number(line.unit_price || 0),
        total_revenue: Number(line.total_revenue || 0),
        inventory_quantity_used: Number(line.inventory_quantity_used || 0),
        servings_per_bottle: line.servings_per_bottle || null,
        nightly_plate_id: line.nightly_plate_id || null,
        plate_name: line.plate_name || null,
        unit_cost: line.unit_cost === null || line.unit_cost === undefined ? null : Number(line.unit_cost),
        total_cogs: line.total_cogs === null || line.total_cogs === undefined ? null : Number(line.total_cogs),
        comp_reason: line.comp_reason || null
      };
    },

    async fetchOpenTableTickets() {
      try {
        const response = await api.get('/table-tickets', {
          params: {
            status: 'open',
            _t: Date.now()
          }
        });

        this.openTableTickets = response.data || [];
      } catch (error) {
        console.error('❌ Error fetching open table tickets:', error);
        this.openTableTickets = [];
      }
    },

    async createTableTicket() {
      const tableName = window.prompt('Table name? Example: Table 4, Bar 2, Patio 1');

      if (!tableName || !tableName.trim()) {
        return;
      }

      try {
        const response = await api.post('/table-tickets', {
          table_name: tableName.trim(),
          notes: this.currentSale.notes || null
        });

        this.activeTableTicket = response.data;
        this.currentTableTicketId = response.data.ticket_id;

        if (this.currentSale.items.length) {
          await this.saveCurrentTicketToTable(false);
        }

        await this.fetchOpenTableTickets();
      } catch (error) {
        console.error('❌ Error creating table ticket:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Could not create table ticket.'
        );
      }
    },

    async openTableTicket(ticketId) {
      if (this.currentSale.items.length && !this.currentTableTicketId) {
        const proceed = window.confirm('This will replace the current unsaved ticket. Continue?');

        if (!proceed) {
          return;
        }
      }

      try {
        const response = await api.get(`/table-tickets/${ticketId}`, {
          params: { _t: Date.now() }
        });

        this.activeTableTicket = response.data;
        this.currentTableTicketId = response.data.ticket_id;
        this.currentSale = {
          ...this.emptySale(),
          notes: response.data.notes || '',
          items: (response.data.lines || []).map(line => this.normalizeTableTicketLine(line))
        };
      } catch (error) {
        console.error('❌ Error opening table ticket:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Could not open table ticket.'
        );
      }
    },

    async saveCurrentTicketToTable(showSavedMessage = true) {
      if (!this.currentSale.items.length) {
        this.showWarningPopup('Add at least one item before saving a table.');
        return false;
      }

      if (!this.currentTableTicketId) {
        await this.createTableTicket();
        return Boolean(this.currentTableTicketId);
      }

      try {
        await api.patch(`/table-tickets/${this.currentTableTicketId}`, {
          notes: this.currentSale.notes || null
        });

        for (const line of this.currentSale.items) {
          const payload = {
            item_no: line.item_no || null,
            item_name: line.item_name || line.description || null,
            description: line.description || null,
            sale_mode: line.sale_mode || null,
            quantity: Number(line.quantity_sold || line.quantity || 1),
            quantity_sold: Number(line.quantity_sold || line.quantity || 1),
            unit_price: Number(line.unit_price || 0),
            total_revenue: Number(line.total_revenue || 0),
            inventory_quantity_used: Number(line.inventory_quantity_used || 0),
            servings_per_bottle: line.servings_per_bottle || null,
            nightly_plate_id: line.nightly_plate_id || null,
            plate_name: line.plate_name || null,
            unit_cost: line.unit_cost === null || line.unit_cost === undefined ? null : Number(line.unit_cost),
            total_cogs: line.total_cogs === null || line.total_cogs === undefined ? null : Number(line.total_cogs),
            comp_reason: line.comp_reason || null
          };

          if (line.ticket_line_id) {
            await api.patch(
              `/table-tickets/${this.currentTableTicketId}/lines/${line.ticket_line_id}`,
              payload
            );
          } else {
            const response = await api.post(
              `/table-tickets/${this.currentTableTicketId}/lines`,
              payload
            );

            line.ticket_line_id = response.data.ticket_line_id;
          }
        }

        await this.fetchOpenTableTickets();

        if (showSavedMessage) {
          this.showWarningPopup(`${this.activeTableTicket?.table_name || 'Table'} saved.`);
        }

        return true;
      } catch (error) {
        console.error('❌ Error saving table ticket:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Could not save table ticket.'
        );

        return false;
      }
    },

    async syncTableTicketLine(line) {
      if (!this.currentTableTicketId || !line?.ticket_line_id) {
        return;
      }

      try {
        await api.patch(`/table-tickets/${this.currentTableTicketId}/lines/${line.ticket_line_id}`, {
          quantity: Number(line.quantity_sold || line.quantity || 1),
          quantity_sold: Number(line.quantity_sold || line.quantity || 1),
          unit_price: Number(line.unit_price || 0)
        });

        await this.fetchOpenTableTickets();
      } catch (error) {
        console.error('❌ Error syncing table ticket line:', error);
      }
    },

    async deleteTableTicketLine(line) {
      if (!this.currentTableTicketId || !line?.ticket_line_id) {
        return;
      }

      try {
        await api.delete(`/table-tickets/${this.currentTableTicketId}/lines/${line.ticket_line_id}`);
        await this.fetchOpenTableTickets();
      } catch (error) {
        console.error('❌ Error deleting table ticket line:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Could not remove table line.'
        );
      }
    },

    closeActiveTableView() {
      this.activeTableTicket = null;
      this.currentTableTicketId = null;
      this.currentSale = this.emptySale();
    },


    loadNightlyPours() {
      try {
        const saved = JSON.parse(localStorage.getItem('winebar_nightly_pours') || '{}');

        this.nightlyPours = {
          red: { ...this.emptyPourWine(), ...(saved.red || {}) },
          white: { ...this.emptyPourWine(), ...(saved.white || {}) },
          rose: { ...this.emptyPourWine(), ...(saved.rose || {}) },
          whiteBubbles: { ...this.emptyPourWine(), ...(saved.whiteBubbles || {}) },
          roseBubbles: { ...this.emptyPourWine(), ...(saved.roseBubbles || {}) }
        };
      } catch (error) {
        this.nightlyPours = {
          red: this.emptyPourWine(),
          white: this.emptyPourWine(),
          rose: this.emptyPourWine(),
          whiteBubbles: this.emptyPourWine(),
          roseBubbles: this.emptyPourWine()
        };
      }
    },

    selectBottleWine() {
      const selected = this.findInventoryItemByNo(this.bottleEntry.item_no);

      if (!selected) {
        this.bottleEntry = this.emptyBottleEntry();
        return;
      }

      this.bottleEntry = {
        ...this.bottleEntry,
        item_no: selected.item_no,
        item_name: selected.item_name,
        description: selected.description || selected.details || ''
      };
    },

    openReserveBottlePopup() {
      this.bottleEntry = {
        ...this.emptyBottleEntry(),
        sale_mode: 'RESERVE_BOTTLE'
      };

      this.showReserveBottlePopup = true;
    },

    addBottleToTicket(mode = 'BOTTLE') {
      if (!this.bottleEntry.item_no) {
        this.showWarningPopup('Choose a bottle first.');
        return;
      }

      if (!this.bottleEntry.quantity || Number(this.bottleEntry.quantity) <= 0) {
        this.showWarningPopup('Enter a valid bottle quantity.');
        return;
      }

      const quantity = Number(this.bottleEntry.quantity || 1);

      let unitPrice;

      if (mode === 'BOTTLE') {
        unitPrice = Number(this.regularBottlePrice);

        if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
          this.showWarningPopup('Enter a valid bottle price.');
          return;
        }
      } else {
        unitPrice = Number(this.bottleEntry.unit_price || 0);

        if (unitPrice <= 0) {
          this.showWarningPopup('Enter a reserve bottle price.');
          return;
        }
      }

      this.addOrIncrementLine({
        item_no: this.bottleEntry.item_no,
        item_name: this.bottleEntry.item_name,
        description: this.bottleEntry.description || '',
        sale_mode: mode,
        quantity_sold: quantity,
        unit_price: unitPrice,
        total_revenue: quantity * unitPrice,
        inventory_quantity_used: quantity,
        servings_per_bottle: null,
        comp_reason: null
      });

      if (mode === 'BOTTLE') {
        this.regularBottlePrice = 47;
      }

      this.bottleEntry = this.emptyBottleEntry();
      this.showReserveBottlePopup = false;
    },

    toggleFoodMenuItem(plateId) {
      const id = Number(plateId);

      this.expandedFoodMenuItemId =
        Number(this.expandedFoodMenuItemId) === id ? null : id;
    },

    async fetchPlateTemplates() {
      try {
        const response = await api.get('/plate-templates');
        this.plateTemplates = response.data || [];

        console.log('🍽️ Plate templates loaded:', this.plateTemplates);
      } catch (error) {
        console.error('❌ Error fetching plate templates:', error);
        this.showWarningPopup('Could not load plate templates.');
      }
    },

    isCurrentDefaultFoodTemplate(template) {
      const name = String(template?.plate_name || '').toLowerCase();

      return name.includes('english') ||
        name.includes('interesting woman') ||
        name.includes('most interesting') ||        
        name.includes('togetherly');
    },

    async openPlateMenuSetupPopup() {
      await this.fetchPlateTemplates();
      await this.fetchNightlyPlates();

      await this.createMissingDefaultNightlyPlates();
      await this.fetchNightlyPlates();

      this.plateMenuDrafts = Array.isArray(this.nightlyPlates)
        ? this.nightlyPlates.map(plate => ({
            ...plate,
            price: Number(plate.price || 18),
            ingredients: Array.isArray(plate.ingredients)
              ? plate.ingredients.map(ingredient => ({
                  ...ingredient,
                  serving_quantity: Number(ingredient.serving_quantity || 0),
                  ingredient_cost: Number(ingredient.ingredient_cost || 0),
                  line_cost: Number(ingredient.line_cost || 0),
                  quantity_on_hand: Number(ingredient.quantity_on_hand || 0)
                }))
              : []
          }))
        : [];

      this.expandedFoodMenuItemId = this.plateMenuDrafts.length
        ? this.plateMenuDrafts[0].nightly_plate_id
        : null;

      this.showPlateMenuPopup = true;
    },

    async createMissingDefaultNightlyPlates() {
      if (!this.plateTemplates.length) {
        await this.fetchPlateTemplates();
      }

      const defaultTemplates = this.plateTemplates.filter(template => {
        return this.isCurrentDefaultFoodTemplate(template);
      });

      const existingTemplateIds = new Set(
        (this.nightlyPlates || [])
          .map(plate => Number(plate.plate_template_id))
          .filter(Boolean)
      );

      for (const template of defaultTemplates) {
        if (existingTemplateIds.has(Number(template.plate_template_id))) {
          continue;
        }

        try {
          await api.post('/nightly-plates/from-template', {
            service_date: this.serviceDate,
            plate_template_id: template.plate_template_id,
            price: Number(template.default_price || 18)
          });
        } catch (error) {
          if (error.response?.status !== 409) {
            console.error('❌ Error creating missing nightly food item:', error);
            throw error;
          }
        }
      }
    },

    async createDefaultNightlyPlateMenu() {
      if (!this.plateTemplates.length) {
        await this.fetchPlateTemplates();
      }

      const defaultTemplates = this.plateTemplates.filter(template => {
        return this.isCurrentDefaultFoodTemplate(template);
      });

      if (!defaultTemplates.length) {
        this.showWarningPopup('No current food templates exist yet.');
        return;
      }

      for (const template of defaultTemplates) {
        try {
          await api.post('/nightly-plates/from-template', {
            service_date: this.serviceDate,
            plate_template_id: template.plate_template_id,
            price: Number(template.default_price || 18)
          });
        } catch (error) {
          if (error.response?.status !== 409) {
            console.error('❌ Error creating nightly food item:', error);
            throw error;
          }
        }
      }
    },

    async savePlateMenuSetup() {
      if (!Array.isArray(this.plateMenuDrafts)) {
        console.error('❌ plateMenuDrafts is not an array:', this.plateMenuDrafts);
        this.showWarningPopup('Food menu was not loaded correctly. Close and reopen Plate Menu.');
        return;
      }

      if (!this.plateMenuDrafts.length) {
        this.showWarningPopup('There are no food items to save.');
        return;
      }

      try {
        for (const plate of this.plateMenuDrafts) {
          await api.put(`/nightly-plates/${plate.nightly_plate_id}`, {
            plate_name: plate.plate_name,
            description: plate.description,
            price: Number(plate.price || 0),
            active: plate.active !== false
          });

          await api.put(`/nightly-plates/${plate.nightly_plate_id}/lines`, {
            ingredients: Array.isArray(plate.ingredients)
              ? plate.ingredients.map(ingredient => ({
                  ingredient_id: ingredient.ingredient_id,
                  serving_quantity: Number(ingredient.serving_quantity || 0),
                  serving_unit: ingredient.serving_unit || 'oz'
                }))
              : []
          });
        }

        await this.fetchNightlyPlates();

        this.showPlateMenuPopup = false;
        this.showWarningPopup('Tonight’s food menu has been saved.');
      } catch (error) {
        console.error('❌ Error saving food menu setup:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Could not save tonight’s food menu.'
        );
      }
    },

    async openCreatePlateMenuPopup() {
      await this.fetchPlateTemplates();

      if (!this.plateTemplates.length) {
        this.showWarningPopup('No food templates exist yet.');
        return;
      }

      this.selectedPlateTemplateIds = this.plateTemplates
        .filter(template => this.isCurrentDefaultFoodTemplate(template))
        .map(template => template.plate_template_id);

      this.showCreatePlateMenuPopup = true;
    },

        togglePlateTemplate(templateId) {
      const id = Number(templateId);

      if (this.selectedPlateTemplateIds.includes(id)) {
        this.selectedPlateTemplateIds = this.selectedPlateTemplateIds.filter(existingId => existingId !== id);
      } else {
        this.selectedPlateTemplateIds.push(id);
      }
    },

    async createNightlyPlateMenu() {
      if (!this.selectedPlateTemplateIds.length) {
        this.showWarningPopup('Choose at least one food template.');
        return;
      }

      try {
        for (const plateTemplateId of this.selectedPlateTemplateIds) {
          const template = this.plateTemplates.find(item => {
            return Number(item.plate_template_id) === Number(plateTemplateId);
          });

          await api.post('/nightly-plates/from-template', {
            service_date: this.serviceDate,
            plate_template_id: plateTemplateId,
            price: Number(template?.default_price || 18)
          });
        }

        this.showCreatePlateMenuPopup = false;

        await this.fetchNightlyPlates();

        if (this.nightlyPlates.length) {
          this.selectedPlate = this.nightlyPlates[0];
          this.plateQuantity = 1;
          this.showPlateMenuPopup = true;
        }

        this.showWarningPopup('Tonight’s food menu has been created.');
      } catch (error) {
        console.error('❌ Error creating nightly food menu:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Could not create tonight’s food menu.'
        );
      }
    },

    async fetchNightlyPlates() {
      try {
        const response = await api.get('/nightly-plates', {
          params: {
            date: this.serviceDate
          }
        });

        this.nightlyPlates = response.data || [];

        console.log('🍽️ Nightly plates loaded:', this.nightlyPlates);
      } catch (error) {
        console.error('❌ Error fetching nightly plates:', error);
        this.showWarningPopup('Could not load tonight’s food menu.');
      }
    },

    findNightlyPlateByName(nameFragment) {
      const fragment = String(nameFragment || '').toLowerCase();

      return this.nightlyPlates.find(plate => {
        return String(plate.plate_name || '').toLowerCase().includes(fragment);
      });
    },

    addPlateEnglish() {
      const plate = this.findNightlyPlateByName('english');

      if (!plate) {
        this.showWarningPopup('Tonight’s English plate has not been created yet. Open Plate Menu first.');
        return;
      }

      this.addConfiguredPlateToTicket(plate);
    },

    addPlateInterestingWoman() {
      const plate =
        this.findNightlyPlateByName('interesting woman') ||
        this.findNightlyPlateByName('most interesting');

      if (!plate) {
        this.showWarningPopup('Tonight’s Interesting Woman plate has not been created yet. Open Plate Menu first.');
        return;
      }

      this.addConfiguredPlateToTicket(plate);
    },

    addPlateTogetherly() {
      const plate = this.findNightlyPlateByName('togetherly');

      if (!plate) {
        this.showWarningPopup('Tonight’s Togetherly has not been created yet. Open Plate Menu first.');
        return;
      }

      this.addConfiguredPlateToTicket(plate);
    },

    
    addPlateVegan() {
      const plate = this.findNightlyPlateByName('vegan');

      if (!plate) {
        this.showWarningPopup('Tonight’s vegan plate has not been created yet. Open Plate Menu first.');
        return;
      }

      this.addConfiguredPlateToTicket(plate);
    },

    addPlate() {
      return this.addPlateEnglish();
    },

    addPlateBig() {
      return this.addPlateTogetherly();
    },

    addConfiguredPlateToTicket(plate) {
      if (!plate?.nightly_plate_id) {
        this.showWarningPopup('This food item is missing its nightly plate ID.');
        return;
      }

      const unitPrice = Number(plate.price || 0);
      const plateCost = Number(plate.plate_cost || 0);

      if (unitPrice <= 0) {
        this.showWarningPopup('Food price must be greater than zero.');
        return;
      }

      const existingIndex = this.currentSale.items.findIndex(item => {
        return item.sale_mode === 'PLATE' &&
          !this.isHiddenWineTourComponent(item) &&
          Number(item.nightly_plate_id) === Number(plate.nightly_plate_id);
      });

      if (existingIndex >= 0) {
        const existing = this.currentSale.items[existingIndex];
        const newQuantity = Number(existing.quantity_sold || existing.quantity || 0) + 1;

        existing.quantity = newQuantity;
        existing.quantity_sold = newQuantity;
        existing.total_revenue = newQuantity * Number(existing.unit_price || unitPrice);
        existing.total_cogs = newQuantity * Number(existing.unit_cost || plateCost);

        this.currentSale.items.splice(existingIndex, 1, existing);
        return;
      }

      this.currentSale.items.push({
        line_key: `PLATE-${plate.nightly_plate_id}`,
        item_no: null,
        item_name: plate.plate_name,
        description: plate.description || '',
        sale_mode: 'PLATE',
        nightly_plate_id: Number(plate.nightly_plate_id),
        plate_name: plate.plate_name,
        quantity: 1,
        quantity_sold: 1,
        unit_price: unitPrice,
        total_revenue: unitPrice,
        inventory_quantity_used: 0,
        unit_cost: plateCost,
        total_cogs: plateCost,
        servings_per_bottle: null,
        comp_reason: null
      });
    },

    saveSquareUrl() {
      localStorage.setItem('winebar_square_url', this.squareCheckoutUrl || '');
    },

  async fetchAvailableWineInventory() {
    try {
      const response = await api.get('/inventory/available-wines');

      this.availableWineInventory = Array.isArray(response.data)
        ? response.data
        : [];

      // Validate previously saved nightly selections
      this.reconcileNightlyPourSelections();

    } catch (error) {
      console.error('Error fetching available wine inventory:', error);
      this.availableWineInventory = [];
      this.showWarningPopup('Unable to load available wine inventory.');
    }
  },


    async fetchSellableRetailItems() {
      try {
        const response = await api.get('/inventory/sellable-retail');
        this.sellableRetailItems = response.data || [];
      } catch (error) {
        console.error('❌ Error loading sellable retail items:', error);
        this.sellableRetailItems = [];
      }
    },

    getRetailItemByNameMatch(...patterns) {
      const lowerPatterns = patterns.map(pattern => String(pattern).toLowerCase());

      return (this.sellableRetailItems || []).find(item => {
        const name = String(item.item_name || '').toLowerCase();
        return lowerPatterns.some(pattern => name.includes(pattern));
      });
    },

    addRetailItemToTicket(item, fallbackPrice = 0) {
      if (!item) {
        this.showWarningPopup('Retail item is not available in inventory.');
        return;
      }

      const quantityOnHand = Number(item.quantity_on_hand || 0);

      if (quantityOnHand < 1) {
        this.showWarningPopup(`${item.item_name} is out of stock.`);
        return;
      }

      const unitPrice = Number(item.default_sale_price || fallbackPrice || 0);
      const unitCost = Number(item.moving_avg_cost || item.item_cost || 0);
      const saleMode = String(item.service_class || '').toLowerCase() === 'merch' ? 'MERCH' : 'RETAIL';

      this.addOrIncrementLine({
        item_no: item.item_no,
        item_name: item.item_name,
        description: item.details || item.description || item.item_name,
        sale_mode: saleMode,
        quantity: 1,
        quantity_sold: 1,
        unit_price: unitPrice,
        total_revenue: unitPrice,
        inventory_quantity_used: 1,
        unit_cost: unitCost,
        total_cogs: unitCost,
        servings_per_bottle: null,
        comp_reason: null,
        nightly_plate_id: null,
        plate_name: null
      });
    },

    addMycoPollenToTicket() {
      const item = this.getRetailItemByNameMatch('myco pollen');
      this.addRetailItemToTicket(item, 5.50);
    },

    addMushroomOatMilkToTicket() {
      const item = this.getRetailItemByNameMatch('mushroom oat milk', 'oat milk');
      this.addRetailItemToTicket(item, 5.50);
    },

    addTeeShirtToTicket() {
      const item = this.getRetailItemByNameMatch('tee', 'shirt');
      this.addRetailItemToTicket(item, 20);
    },

    addFizzyWaterToTicket() {
      const item = this.getRetailItemByNameMatch('fizzy', 'la croix');
      this.addRetailItemToTicket(item, 2);
    },

    findInventoryItemByNo(itemNo) {
      return this.availableWineInventory.find(item => String(item.item_no) === String(itemNo));
    },

    selectPourWine(slotKey) {
      const selected = this.eligibleWinesForSlot(slotKey).find(item =>
        String(item.item_no) === String(this.nightlyPours[slotKey].item_no)
      );

      if (!selected) {
        this.nightlyPours[slotKey] = this.emptyPourWine();
        return;
      }

      this.nightlyPours[slotKey] = {
        item_no: selected.item_no,
        item_name: selected.item_name,
        description: selected.description || selected.details || '',
        servings_per_bottle: selected.servings_per_bottle || SERVINGS_PER_BOTTLE
      };
    },

    clearPourSlot(slotKey) {
      this.nightlyPours[slotKey] = this.emptyPourWine();
    },

    addGlass(slotKey, options = {}) {
      const wine = this.nightlyPours[slotKey];
      const isHalfGlass = options.half === true;

      if (!wine.item_no) {
        this.showWarningPopup('Choose a wine for this pour first.');
        return;
      }

      // Verify wine belongs to the selected category
      const validForSlot = this.eligibleWinesForSlot(slotKey).some(item =>
        String(item.item_no) === String(wine.item_no)
      );

      if (!validForSlot) {
        this.showWarningPopup(
          'This wine does not belong to the selected pour category. Choose another wine.'
        );
        return;
      }

      const unitPrice = isHalfGlass ? 7.50 : Number(this.glassPrice || 0);

      if (unitPrice <= 0) {
        this.showWarningPopup('Set a valid glass price before ringing this pour.');
        return;
      }

      const servingsPerBottle = Number(wine.servings_per_bottle || SERVINGS_PER_BOTTLE);
      const inventoryUsed = isHalfGlass
        ? Number((0.5 / servingsPerBottle).toFixed(4))
        : Number((1 / servingsPerBottle).toFixed(4));

      this.addOrIncrementLine({
        item_no: wine.item_no,
        item_name: isHalfGlass ? `${wine.item_name} - Half Glass` : wine.item_name,
        description: isHalfGlass
          ? `Half glass - ${wine.description || wine.item_name || ''}`
          : wine.description || '',
        sale_mode: 'GLASS',
        quantity: 1,
        quantity_sold: 1,
        unit_price: unitPrice,
        total_revenue: unitPrice,
        inventory_quantity_used: inventoryUsed,
        servings_per_bottle: isHalfGlass ? servingsPerBottle * 2 : servingsPerBottle,
        comp_reason: null
      });
    },

    addHalfGlass(slotKey) {
      this.addGlass(slotKey, { half: true });
    },

    addOrIncrementLine(newLine) {
      const existing = this.currentSale.items.find(line =>
        String(line.item_no) === String(newLine.item_no) &&
        line.sale_mode === newLine.sale_mode &&
        Number(line.unit_price || 0) === Number(newLine.unit_price || 0)
      );

      if (existing) {
        existing.quantity_sold += newLine.quantity_sold;
        this.recalculateLine(existing);
      } else {
        this.currentSale.items.push(newLine);
      }
    },

    async incrementLine(index) {
      const line = this.currentSale.items[index];

      if (!line) return;

      if (line.sale_mode === 'WINE_TOUR') {
        return;
      }

      line.quantity_sold += 1;
      line.quantity = line.quantity_sold;
      this.recalculateLine(line);

      await this.syncTableTicketLine(line);
    },

    async decrementLine(index) {
      const line = this.currentSale.items[index];

      if (!line) return;

      if (line.sale_mode === 'WINE_TOUR') {
        const groupId = line.wine_tour_group_id || line.line_key;

        this.currentSale.items = this.currentSale.items.filter((item) => {
          const description = String(item.description || '');
          return item.line_key !== groupId &&
            item.wine_tour_group_id !== groupId &&
            !description.includes(`__WINE_TOUR_COMPONENT__ ${groupId}`);
        });

        return;
      }

      line.quantity_sold -= 1;
      line.quantity = line.quantity_sold;

      if (line.quantity_sold <= 0) {
        this.currentSale.items.splice(index, 1);
        await this.deleteTableTicketLine(line);
        return;
      }

      this.recalculateLine(line);
      await this.syncTableTicketLine(line);
    },

    recalculateLine(line) {
      const quantity = Number(line.quantity_sold || 0);
      const unitPrice = Number(line.unit_price || 0);
      const unitCost = line.unit_cost === null || line.unit_cost === undefined ? null : Number(line.unit_cost || 0);
      const saleMode = String(line.sale_mode || '').toUpperCase();

      line.total_revenue = quantity * unitPrice;
      line.total_cogs = unitCost === null ? null : quantity * unitCost;

      if (saleMode === 'GLASS') {
        if (this.isHiddenWineTourComponent(line)) {
          return;
        }

        line.inventory_quantity_used = Number(
          (quantity / Number(line.servings_per_bottle || SERVINGS_PER_BOTTLE)).toFixed(4)
        );
      } else if (['BOTTLE', 'RESERVE_BOTTLE', 'MERCH', 'RETAIL'].includes(saleMode)) {
        line.inventory_quantity_used = quantity;
      } else if (saleMode === 'WINE_TOUR' || saleMode === 'MISC') {
        line.inventory_quantity_used = 0;
      }
    },

    clearCurrentSale() {
      this.currentSale = this.emptySale();
      this.activeTableTicket = null;
      this.currentTableTicketId = null;
    },



    // Wine Tour calls

    isHiddenWineTourComponent(line) {
      return String(line.description || '').startsWith('__WINE_TOUR_COMPONENT__');
    },

    getWineTourWineByItemNo(itemNo) {
      return (this.wineTourOptions?.wines || []).find((wine) => {
        return Number(wine.item_no) === Number(itemNo);
      }) || null;
    },

    getWineTourNightlyPlateById(nightlyPlateId) {
      return (this.wineTourOptions?.plates || []).find((plate) => {
        return Number(plate.nightly_plate_id) === Number(nightlyPlateId);
      }) || null;
    },

    async loadWineTourOptions() {
      this.wineTourLoading = true;

      try {
        const response = await api.get('/wine-tour/options');

        await this.fetchNightlyPlates();

        if (!Array.isArray(this.nightlyPlates) || !this.nightlyPlates.length) {
          await this.createMissingDefaultNightlyPlates();
          await this.fetchNightlyPlates();
        }

        const wines = response.data?.wines || [];
        const defaults = response.data?.defaults || [];
        const nightlyPlates = Array.isArray(this.nightlyPlates) ? this.nightlyPlates : [];

        const togetherlyPlate =
          nightlyPlates.find((plate) => {
            return String(plate.plate_name || '').toLowerCase().includes('togetherly');
          }) || null;

        this.wineTourOptions = {
          product: response.data?.product || { sale_price: 30 },
          wines,
          plates: togetherlyPlate ? [togetherlyPlate] : [],
          defaults
        };

        const whiteBubbles = defaults.find((d) => d.component_label === 'WHITE_BUBBLES');
        const whiteFlat = defaults.find((d) => d.component_label === 'WHITE_FLAT');
        const red = defaults.find((d) => d.component_label === 'RED');

        this.wineTourForm.white_bubbles_item_no =
          whiteBubbles?.item_no || wines[0]?.item_no || null;

        this.wineTourForm.white_flat_item_no =
          whiteFlat?.item_no || wines[1]?.item_no || wines[0]?.item_no || null;

        this.wineTourForm.red_item_no =
          red?.item_no || wines[2]?.item_no || wines[0]?.item_no || null;

        this.wineTourForm.nightly_plate_id = togetherlyPlate?.nightly_plate_id || null;
        this.wineTourForm.plate_template_id = togetherlyPlate?.plate_template_id || null;

        if (!togetherlyPlate) {
          this.showWarningPopup('Togetherly is not available for tonight. Open Plate Menu first so the Wine Tour can use Togetherly.');
        }
      } catch (err) {
        console.error('❌ Error loading Wine Tour options:', err);
        this.showWarningPopup(
          err.response?.data?.details ||
          err.response?.data?.error ||
          err.message ||
          'Could not load Wine Tour options.'
        );
      } finally {
        this.wineTourLoading = false;
      }
    },

    async openWineTourModal() {
      this.showWineTourModal = true;
      this.wineTourForm.quantity = 1;
      this.wineTourForm.save_as_default = false;

      await this.loadWineTourOptions();
    },

    closeWineTourModal() {
      if (this.wineTourSubmitting) return;

      this.showWineTourModal = false;
      this.wineTourLoading = false;
    },

    getWineTourWineName(itemNo) {
      const wine = this.getWineTourWineByItemNo(itemNo);
      return wine?.item_name || '';
    },

    getWineTourPlateName(nightlyPlateId) {
      const plate = this.getWineTourNightlyPlateById(nightlyPlateId);
      return plate?.plate_name || '';
    },

    getWineTourEstimatedTotal() {
      const price = Number(this.wineTourOptions.product?.sale_price || 30);
      const qty = Math.max(1, Number(this.wineTourForm.quantity || 1));
      return price * qty;
    },

    async addWineTourToTicket() {
      if (this.wineTourSubmitting) return;

      const quantity = Math.max(1, Math.round(Number(this.wineTourForm.quantity || 1)));

      if (
        !this.wineTourForm.white_bubbles_item_no ||
        !this.wineTourForm.white_flat_item_no ||
        !this.wineTourForm.red_item_no
      ) {
        this.showWarningPopup('Please select all three Wine Tour wines.');
        return;
      }

      if (!this.wineTourForm.nightly_plate_id) {
        this.showWarningPopup('Togetherly is not available for tonight. Open Plate Menu first, then try Wine Tour again.');
        return;
      }

      const whiteBubbles = this.getWineTourWineByItemNo(this.wineTourForm.white_bubbles_item_no);
      const whiteFlat = this.getWineTourWineByItemNo(this.wineTourForm.white_flat_item_no);
      const red = this.getWineTourWineByItemNo(this.wineTourForm.red_item_no);
      const plate = this.getWineTourNightlyPlateById(this.wineTourForm.nightly_plate_id);

      if (!whiteBubbles || !whiteFlat || !red || !plate) {
        this.showWarningPopup('One or more Wine Tour components could not be found.');
        return;
      }

      this.wineTourSubmitting = true;

      try {
        const groupId = `WT-${Date.now()}`;
        const tourPrice = Number(this.wineTourOptions.product?.sale_price || 30);
        const pourBottleQtyPerTour = Number((1.5 / 25.36).toFixed(4));

        const wineComponentLine = (label, wine) => {
          const bottleQtyUsed = Number((pourBottleQtyPerTour * quantity).toFixed(4));
          const bottleCost = Number(wine.moving_avg_cost || wine.item_cost || wine.cost || 0);
          const componentCost = Number((bottleCost * bottleQtyUsed).toFixed(4));

          return {
            line_key: `${groupId}-${label}`,
            item_no: wine.item_no,
            item_name: `Wine Tour ${label}`,
            description: `__WINE_TOUR_COMPONENT__ ${groupId} ${label}: ${wine.item_name}`,
            sale_mode: 'GLASS',
            quantity,
            quantity_sold: quantity,
            unit_price: 0,
            total_revenue: 0,
            inventory_quantity_used: bottleQtyUsed,
            servings_per_bottle: null,
            nightly_plate_id: null,
            plate_name: null,
            unit_cost: Number((componentCost / quantity).toFixed(4)),
            total_cogs: componentCost,
            comp_reason: 'Wine Tour component'
          };
        };

        const plateCost = Number(plate.plate_cost || plate.estimated_cogs || 0);
        const totalPlateCost = Number((plateCost * quantity).toFixed(4));

        const visibleTourLine = {
          line_key: groupId,
          item_no: null,
          item_name: 'Wine Tour',
          description: [
            `White bubbles: ${whiteBubbles.item_name}`,
            `White flat: ${whiteFlat.item_name}`,
            `Red: ${red.item_name}`,
            `Plate: ${plate.plate_name}`
          ].join(' | '),
          sale_mode: 'WINE_TOUR',
          quantity,
          quantity_sold: quantity,
          unit_price: tourPrice,
          total_revenue: Number((tourPrice * quantity).toFixed(2)),
          inventory_quantity_used: 0,
          servings_per_bottle: null,
          nightly_plate_id: null,
          plate_name: null,
          unit_cost: 0,
          total_cogs: 0,
          comp_reason: null,
          wine_tour_group_id: groupId
        };

        const plateComponentLine = {
          line_key: `${groupId}-PLATE`,
          item_no: null,
          item_name: 'Wine Tour Plate',
          description: `__WINE_TOUR_COMPONENT__ ${groupId} Plate: ${plate.plate_name}`,
          sale_mode: 'PLATE',
          quantity,
          quantity_sold: quantity,
          unit_price: 0,
          total_revenue: 0,
          inventory_quantity_used: 0,
          servings_per_bottle: null,
          nightly_plate_id: Number(plate.nightly_plate_id),
          plate_name: plate.plate_name,
          unit_cost: plateCost,
          total_cogs: totalPlateCost,
          comp_reason: 'Wine Tour component'
        };

        this.currentSale.items.push(
          visibleTourLine,
          wineComponentLine('White Bubbles', whiteBubbles),
          wineComponentLine('White Flat', whiteFlat),
          wineComponentLine('Red', red),
          plateComponentLine
        );

        if (this.wineTourForm.save_as_default) {
          await api.post('/wine-tour/defaults', {
            selections: {
              white_bubbles_item_no: Number(this.wineTourForm.white_bubbles_item_no),
              white_flat_item_no: Number(this.wineTourForm.white_flat_item_no),
              red_item_no: Number(this.wineTourForm.red_item_no),
              plate_template_id: Number(plate.plate_template_id || this.wineTourForm.plate_template_id)
            }
          });
        }

        this.showWineTourModal = false;
      } catch (err) {
        console.error('❌ Error adding Wine Tour to ticket:', err);
        this.showWarningPopup(
          err.response?.data?.details ||
          err.response?.data?.error ||
          err.message ||
          'Could not add Wine Tour to ticket.'
        );
      } finally {
        this.wineTourSubmitting = false;
      }
    },

    openMiscSalePopup() {
      this.miscSale = {
        description: '',
        amount: null,
        cost: null
      };

      this.showMiscSalePopup = true;
    },

    closeMiscSalePopup() {
      this.showMiscSalePopup = false;
    },

    addMiscSaleToTicket() {
      const amount = Number(this.miscSale.amount || 0);
      const description = String(this.miscSale.description || '').trim();
      const hasKnownCost =
        this.miscSale.cost !== null &&
        this.miscSale.cost !== '' &&
        Number.isFinite(Number(this.miscSale.cost));

      if (!description) {
        this.showWarningPopup('Enter a description for the misc sale.');
        return;
      }

      if (amount <= 0) {
        this.showWarningPopup('Enter a misc sale amount greater than zero.');
        return;
      }

      const unitCost = hasKnownCost ? Number(this.miscSale.cost) : null;

      this.currentSale.items.push({
        line_key: `MISC-${Date.now()}`,
        item_no: null,
        item_name: description,
        description,
        sale_mode: 'MISC',
        quantity: 1,
        quantity_sold: 1,
        unit_price: amount,
        total_revenue: amount,
        inventory_quantity_used: 0,
        servings_per_bottle: null,
        nightly_plate_id: null,
        plate_name: null,
        unit_cost: unitCost,
        total_cogs: unitCost,
        comp_reason: null
      });

      this.showMiscSalePopup = false;
    },

    openCashTenderPopup() {
      if (!this.currentSale.items.length) {
        this.showWarningPopup('Add at least one item before taking cash.');
        return;
      }

      this.cashTender = {
        show: true,
        cash_received: Number(this.cartTotal || 0),
        working: false
      };
    },

    closeCashTenderPopup() {
      if (this.cashTender.working) return;

      this.cashTender = {
        show: false,
        cash_received: null,
        working: false
      };
    },

    async submitCashTender() {
      if (!this.currentSale.items.length || this.cashTender.working) {
        return;
      }

      const total = Number(this.cartTotal || 0);
      const cashReceived = Number(this.cashTender.cash_received || 0);

      if (!Number.isFinite(cashReceived) || cashReceived < total) {
        this.showWarningPopup('Cash received must be at least the sale total.');
        return;
      }

      this.cashTender.working = true;

      try {
        const changeDue = Number((cashReceived - total).toFixed(2));

        if (this.currentTableTicketId) {
          const saved = await this.saveCurrentTicketToTable(false);

          if (!saved) {
            return;
          }

          const response = await api.post(`/table-tickets/${this.currentTableTicketId}/close-cash`, {
            cash_received: cashReceived
          });

          this.cashTender = {
            show: false,
            cash_received: null,
            working: false
          };

          this.clearCurrentSale();

          await this.fetchOpenTableTickets();
          await this.fetchAvailableWineInventory();
        await this.fetchSellableRetailItems();
          await this.fetchSales();
          await this.fetchOpenTableTickets();

          this.showWarningPopup(`Cash sale complete. Change due: $${this.money(response.data?.change_due ?? changeDue)}.`);
          return;
        }

        await this.completeSale('Cash', {
          cash_received: cashReceived,
          change_due: changeDue
        });

        this.cashTender = {
          show: false,
          cash_received: null,
          working: false
        };

        this.showWarningPopup(`Cash sale complete. Change due: $${this.money(changeDue)}.`);
      } catch (error) {
        console.error('❌ Error completing cash tender:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Unable to complete cash sale.'
        );
      } finally {
        this.cashTender.working = false;
      }
    },

    async completeSale(paymentMethod, paymentInfo = {}) {
      if (!this.currentSale.items.length) return;

      try {
        const payload = {
          sale_date: this.serviceDate,
          payment_method: paymentMethod,
          payment_status: 'paid',
          notes: this.currentSale.notes,
          cash_received: paymentInfo.cash_received ?? null,
          change_due: paymentInfo.change_due ?? null,
          sale_type: this.deriveSaleType(this.currentSale.items),
          total_sale: this.cartTotal,
          items: this.currentSale.items.map(line => ({
            item_no: line.item_no,
            item_name: line.item_name,
            description: line.description,
            sale_mode: line.sale_mode,
            quantity: line.quantity || line.quantity_sold,
            quantity_sold: line.quantity_sold,
            unit_price: line.unit_price,
            total_revenue: line.total_revenue,
            inventory_quantity_used: line.inventory_quantity_used,
            unit_cost: line.unit_cost === null || line.unit_cost === undefined ? null : Number(line.unit_cost),
            total_cogs: line.total_cogs === null || line.total_cogs === undefined ? null : Number(line.total_cogs),
            nightly_plate_id: line.nightly_plate_id || null,
            plate_name: line.plate_name || null,
            comp_reason: line.comp_reason || null
          }))
        };

        console.log('🧾 Cash sale payload:', JSON.stringify(payload, null, 2));

        await api.post('/sales', payload);

        this.clearCurrentSale();
        await this.fetchAvailableWineInventory();
        await this.fetchSellableRetailItems();
        await this.fetchSales();

      } catch (error) {
        console.error('Error completing sale:', error);
        this.showWarningPopup(error.response?.data?.message || error.response?.data?.error || 'Unable to complete sale.');
      }
    },

    async completeSquareSale() {
      if (!this.currentSale.items.length) {
        this.showWarningPopup('Add at least one item before checking out.');
        return;
      }

      try {
        let saleId = null;

        if (this.currentTableTicketId) {
          const saved = await this.saveCurrentTicketToTable(false);

          if (!saved) {
            return;
          }

          const closeResponse = await api.post(`/table-tickets/${this.currentTableTicketId}/close-square`);
          saleId = closeResponse.data?.sale_id;
        } else {
          const salePayload = {
            sale_date: this.serviceDate,
            payment_method: 'Square',
            payment_status: 'pending',
            notes: this.currentSale.notes || '',
            items: this.currentSale.items
          };

          console.log('📤 Creating pending Square sale:', salePayload);

          const saleResponse = await api.post('/sales', salePayload);

          saleId =
            saleResponse.data?.sale_id ||
            saleResponse.data?.sale?.sale_id;
        }

        if (!saleId) {
          this.showWarningPopup('Square sale was created, but no sale ID was returned.');
          return;
        }

        console.log('✅ Pending Square sale created:', saleId);

        const squareResponse = await api.post('/square/start-pos-payment', {
          sale_id: saleId
        });

        const squareUrl = squareResponse.data?.square_url;

        if (!squareUrl) {
          console.error('❌ Backend did not return Square URL:', squareResponse.data);
          this.showWarningPopup('Could not start Square payment.');
          return;
        }

        console.log('➡️ Opening Square POS:', squareUrl);

        localStorage.setItem('winebar_pending_square_sale_id', String(saleId));
        localStorage.setItem('winebar_pending_square_started_at', new Date().toISOString());

        this.squareCompletionSaleId = saleId;
        this.showSquareCompletionPopup = true;

        window.location.href = squareUrl;

      } catch (error) {
        console.error('❌ Error starting Square sale:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Unable to start Square payment.'
        );
      }
    },

    async checkPendingSquareSale(showPendingMessage = false) {
      const pendingSaleId =
        this.squareCompletionSaleId ||
        localStorage.getItem('winebar_pending_square_sale_id');

      if (!pendingSaleId || this.handlingSquareReturn) {
        return false;
      }

      this.handlingSquareReturn = true;

      try {
        const response = await api.get(`/sales/${pendingSaleId}/payment-status`);
        const sale = response.data || {};

        console.log('🔎 Pending Square sale status:', sale);

        const paymentStatus = String(sale.payment_status || '').toLowerCase();
        const squareStatus = String(sale.square_status || '').toLowerCase();

        if (paymentStatus === 'paid' || squareStatus === 'paid') {
          localStorage.removeItem('winebar_pending_square_sale_id');
          localStorage.removeItem('winebar_pending_square_started_at');

          this.showSquareCompletionPopup = false;
          this.squareCompletionSaleId = null;

          this.clearCurrentSale();

          await this.fetchAvailableWineInventory();
        await this.fetchSellableRetailItems();
          await this.fetchSales();

          this.showWarningPopup(`Square payment completed for sale #${pendingSaleId}.`);
          return true;
        }

        if (
          paymentStatus === 'canceled' ||
          paymentStatus === 'failed' ||
          squareStatus === 'canceled' ||
          squareStatus === 'failed'
        ) {
          localStorage.removeItem('winebar_pending_square_sale_id');
          localStorage.removeItem('winebar_pending_square_started_at');

          this.showSquareCompletionPopup = false;
          this.squareCompletionSaleId = null;

          await this.fetchSales();

          this.showWarningPopup(`Square payment ${paymentStatus || squareStatus} for sale #${pendingSaleId}.`);
          return true;
        }

        await this.fetchSales();
        await this.fetchAvailableWineInventory();
        await this.fetchSellableRetailItems();

        if (showPendingMessage) {
          this.showWarningPopup(
            `Sale #${pendingSaleId} is still pending in LotLedger. If Square shows complete, wait a moment and tap Yes again.`
          );
        }

        return false;

      } catch (error) {
        console.error('❌ Error checking pending Square sale:', error);

        if (showPendingMessage) {
          this.showWarningPopup(
            error.response?.data?.details ||
            error.response?.data?.error ||
            'Could not check Square payment status.'
          );
        }

        return false;
      } finally {
        this.handlingSquareReturn = false;
      }
    },

    async handleSquareCompletedYes() {
      const pendingSaleId =
        this.squareCompletionSaleId ||
        localStorage.getItem('winebar_pending_square_sale_id');

      if (!pendingSaleId || this.squareCompletionWorking) {
        return;
      }

      this.squareCompletionWorking = true;

      try {
        console.log('✅ Manually confirming Square sale paid:', pendingSaleId);

        await api.post(`/sales/${pendingSaleId}/confirm-square-paid`);

        localStorage.removeItem('winebar_pending_square_sale_id');
        localStorage.removeItem('winebar_pending_square_started_at');

        this.showSquareCompletionPopup = false;
        this.squareCompletionSaleId = null;

        this.clearCurrentSale();

        await this.fetchAvailableWineInventory();
        await this.fetchSellableRetailItems();
        await this.fetchSales();
        await this.fetchOpenTableTickets();

        this.showWarningPopup(`Square sale #${pendingSaleId} confirmed and moved to history.`);

      } catch (error) {
        console.error('❌ Error confirming Square sale:', error);

        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Could not confirm Square sale. Check backend logs.'
        );
      } finally {
        this.squareCompletionWorking = false;
      }
    },

    async handleSquareCompletedNo() {
      const pendingSaleId =
        this.squareCompletionSaleId ||
        localStorage.getItem('winebar_pending_square_sale_id');

      localStorage.removeItem('winebar_pending_square_sale_id');
      localStorage.removeItem('winebar_pending_square_started_at');

      this.showSquareCompletionPopup = false;
      this.squareCompletionSaleId = null;
      this.squareCompletionWorking = false;

      await this.fetchAvailableWineInventory();
        await this.fetchSellableRetailItems();
      await this.fetchSales();
      await this.fetchOpenTableTickets();

      if (pendingSaleId) {
        this.showWarningPopup(`Square confirmation dismissed for sale #${pendingSaleId}. Sales history refreshed.`);
      }
    },

    closeSquareCompletionPopup() {
      this.showSquareCompletionPopup = false;
    },

    handleSquareReturn() {
      const squareResult = this.$route?.query?.square_result;
      const saleId = this.$route?.query?.sale_id;

      if (!squareResult) {
        return;
      }

      localStorage.removeItem('winebar_pending_square_sale_id');
      localStorage.removeItem('winebar_pending_square_started_at');

      this.showSquareCompletionPopup = false;
      this.squareCompletionSaleId = null;

      if (squareResult === 'paid') {
        this.showWarningPopup(`Square payment completed for sale #${saleId}.`);
        this.clearCurrentSale();
      } else if (squareResult === 'canceled') {
        this.showWarningPopup(`Square payment canceled for sale #${saleId}.`);
      } else if (squareResult === 'failed') {
        this.showWarningPopup(`Square payment failed for sale #${saleId}.`);
      } else {
        this.showWarningPopup('Square returned an unknown payment result.');
      }

      this.fetchAvailableWineInventory();
      this.fetchSales();
      this.fetchOpenTableTickets();

      this.$router.replace({
        path: this.$route.path,
        query: {}
      });
    },

    async fetchSales() {
      try {
        const response = await api.get(`/sales?date=${encodeURIComponent(this.serviceDate)}`);
        this.sales = response.data || [];
      } catch (error) {
        console.error('Error fetching sales:', error);
        this.sales = [];
      }
    },

    deleteSale(saleId) {
      this.saleToDelete = saleId;
      this.showDeletePopup = true;
    },

    async deleteSaleConfirmed() {
      try {
        await api.delete(`/sales/${this.saleToDelete}`);
        this.showDeletePopup = false;
        this.saleToDelete = null;
        await this.fetchAvailableWineInventory();
        await this.fetchSellableRetailItems();
        await this.fetchSales();
      } catch (error) {
        console.error('Error deleting sale:', error);
        this.showDeletePopup = false;
        this.showWarningPopup(error.response?.data?.message || 'Unable to delete sale.');
      }
    },

    blankAdjustmentData(sale = null) {
      return {
        sale_id: sale?.sale_id || null,
        sale_line_id: null,
        item_no: null,
        adjustment_type: 'refund',
        reason: '',
        refund_amount: 0,
        quantity: 0,
        inventory_action: 'none',
        inventory_quantity: 0,
        notes: ''
      };
    },

    async openAdjustmentPopup(sale) {
      this.saleToAdjust = sale;
      this.adjustmentData = this.blankAdjustmentData(sale);
      this.saleLinesForAdjustment = [];

      try {
        const response = await api.get(`/sale-lines/${sale.sale_id}`);
        this.saleLinesForAdjustment = response.data || [];
      } catch (error) {
        console.error('❌ Error fetching sale lines for adjustment:', error);
        this.saleLinesForAdjustment = [];
      }

      this.showAdjustmentPopup = true;
    },

    closeAdjustmentPopup() {
      this.showAdjustmentPopup = false;
      this.saleToAdjust = null;
      this.saleLinesForAdjustment = [];
      this.adjustmentData = this.blankAdjustmentData();
    },

    handleAdjustmentLineChange() {
      const selectedLine = this.saleLinesForAdjustment.find(line => {
        return Number(line.sale_line_id) === Number(this.adjustmentData.sale_line_id);
      });

      if (!selectedLine) {
        this.adjustmentData.item_no = null;
        this.adjustmentData.quantity = 0;
        this.adjustmentData.inventory_quantity = 0;
        this.adjustmentData.inventory_action = 'none';
        return;
      }

      const quantitySold = Number(selectedLine.quantity_sold || selectedLine.quantity || 0);
      const inventoryUsed = Number(selectedLine.inventory_quantity_used || 0);
      const lineRevenue = Number(selectedLine.total_revenue || 0);

      this.adjustmentData.item_no = selectedLine.item_no || null;
      this.adjustmentData.quantity = quantitySold;
      this.adjustmentData.refund_amount = lineRevenue;

      if (selectedLine.sale_mode === 'PLATE') {
        this.adjustmentData.inventory_action = 'none';
        this.adjustmentData.inventory_quantity = 0;
      } else {
        this.adjustmentData.inventory_quantity = inventoryUsed;
      }
    },

    async submitSaleAdjustment() {
      if (!this.saleToAdjust?.sale_id) {
        this.showWarningPopup('No sale selected.');
        return;
      }

      if (!this.adjustmentData.adjustment_type) {
        this.showWarningPopup('Choose an adjustment type.');
        return;
      }

      if (!this.adjustmentData.reason) {
        this.showWarningPopup('Choose a reason.');
        return;
      }

      if (
        this.adjustmentData.inventory_action === 'return_to_stock' &&
        (!this.adjustmentData.item_no || Number(this.adjustmentData.inventory_quantity || 0) <= 0)
      ) {
        this.showWarningPopup('Returning inventory requires an item and inventory quantity.');
        return;
      }

      try {
        const payload = {
          sale_line_id: this.adjustmentData.sale_line_id || null,
          item_no: this.adjustmentData.item_no || null,
          adjustment_type: this.adjustmentData.adjustment_type,
          reason: this.adjustmentData.reason,
          quantity: Number(this.adjustmentData.quantity || 0),
          refund_amount: Number(this.adjustmentData.refund_amount || 0),
          inventory_action: this.adjustmentData.inventory_action || 'none',
          inventory_quantity: Number(this.adjustmentData.inventory_quantity || 0),
          notes: this.adjustmentData.notes || null
        };

        await api.post(`/sales/${this.saleToAdjust.sale_id}/adjustments`, payload);

        this.showAdjustmentPopup = false;

        await this.fetchAvailableWineInventory();
        await this.fetchSellableRetailItems();
        await this.fetchSales();

        this.showWarningPopup('Sale adjustment recorded.');
      } catch (error) {
        console.error('❌ Error saving sale adjustment:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Unable to save sale adjustment.'
        );
      }
    },

    async openAdjustmentHistoryPopup(sale) {
      this.saleToAdjust = sale;
      this.saleAdjustments = [];

      try {
        const response = await api.get(`/sales/${sale.sale_id}/adjustments`);
        this.saleAdjustments = response.data || [];
      } catch (error) {
        console.error('❌ Error fetching sale adjustments:', error);
        this.saleAdjustments = [];
      }

      this.showAdjustmentHistoryPopup = true;
    },

    formatAdjustmentType(type) {
      const labels = {
        refund: 'Refund',
        return: 'Return',
        replacement: 'Replacement',
        writeoff: 'Writeoff'
      };

      return labels[type] || type || 'Adjustment';
    },

    formatInventoryAction(action) {
      const labels = {
        none: 'No inventory impact',
        return_to_stock: 'Returned to stock',
        dumped_faulty: 'Dumped / faulty'
      };

      return labels[action] || action || '—';
    },

    deriveSaleType(items) {
      const modes = [...new Set(items.map(line => line.sale_mode))];
      return modes.length === 1 ? modes[0] : 'MIXED';
    },

    displaySaleMode(mode) {
      const labels = {
        GLASS: 'Glass',
        PLATE: 'Food',
        BOTTLE: 'Bottle',
        RESERVE_BOTTLE: 'Reserve Bottle',
        RETAIL: 'Retail',
        MERCH: 'Merch',
        MISC: 'Misc',
        WINE_TOUR: 'Wine Tour',
        MIXED: 'Mixed'
      };

      return labels[mode] || mode || '—';
    },

    money(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(value || 0));
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toISOString().split('T')[0];
    },

    showWarningPopup(message) {
      this.warningMessage = message;
      this.showWarning = true;
    }
  }
};
</script>
<style scoped>
.sales-shell {
  min-height: 100vh;
  padding: 28px;
  color: #f7efe8;
  font-family: "Inter", "Poppins", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.38), transparent 34%),
    radial-gradient(circle at bottom right, rgba(194, 74, 47, 0.18), transparent 32%),
    linear-gradient(135deg, #0d090d 0%, #171016 42%, #24141b 100%);
}

.sales-hero,
.night-panel,
.register-card,
.history-panel {
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.035));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(18px);
}

.sales-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 22px;
  padding: 30px;
}

.eyebrow,
.label {
  margin: 0 0 8px;
  color: #c8a66a;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 12px;
  font-weight: 900;
}

.label {
  color: #9b8f8b;
  letter-spacing: 0.12em;
}

.sales-hero h1 {
  margin: 0;
  font-size: clamp(40px, 7vw, 72px);
  line-height: 0.92;
  letter-spacing: -0.07em;
}

.hero-copy {
  max-width: 620px;
  margin: 16px 0 0;
  color: #cfc2bd;
  font-size: 16px;
  line-height: 1.5;
}

.hero-date {
  min-width: 220px;
  display: grid;
  gap: 8px;
}

.hero-date span,
.sales-shell label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.night-panel {
  margin-top: 20px;
  padding: 24px;
}

.night-prices {
  display: grid;
  grid-template-columns: 180px minmax(180px, auto);
  gap: 14px;
  margin-bottom: 18px;
  align-items: end;
}

.square-link-field {
  min-width: 260px;
}

.pour-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(180px, 1fr));
  gap: 14px;
}

.pour-card {
  padding: 16px;
  border-radius: 24px;
  background: rgba(0, 0, 0, 0.24);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.pour-card-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.pour-card h2 {
  margin: 0;
  font-size: 20px;
  line-height: 1.05;
  letter-spacing: -0.04em;
}

.mini-pill,
.status-pill {
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  justify-content: center;
  padding: 0 13px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 900;
  color: #f1c979;
  background: rgba(200, 166, 106, 0.16);
  border: 1px solid rgba(200, 166, 106, 0.28);
  white-space: nowrap;
}

.pour-card-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  margin-top: 12px;
}

.register-grid {
  display: grid;
  grid-template-columns: minmax(320px, 0.85fr) minmax(420px, 1.15fr);
  gap: 18px;
  margin-top: 18px;
}

.register-card {
  padding: 24px;
}

.section-heading h2,
.ticket-header h2,
.history-header h2 {
  margin: 0;
  font-size: clamp(28px, 4vw, 44px);
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.fast-buttons {
  display: grid;
  gap: 12px;
  margin-top: 18px;
}

.fast-sale-button {
  min-height: 84px;
  border: 0;
  border-radius: 24px;
  padding: 16px;
  text-align: left;
  cursor: pointer;
  color: #fff8f1;
  background:
    radial-gradient(circle at top left, rgba(143, 18, 63, 0.28), transparent 38%),
    rgba(0, 0, 0, 0.26);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: transform 0.14s ease, box-shadow 0.14s ease;
}

.fast-sale-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.28);
}

.fast-sale-button:disabled {
  opacity: 0.42;
  cursor: not-allowed;
  transform: none;
}

.fast-sale-button span {
  display: block;
  color: #c8a66a;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.14em;
}

.fast-sale-button strong {
  display: block;
  margin-top: 8px;
  font-size: 26px;
  letter-spacing: -0.05em;
}

.plate-button {
  background:
    radial-gradient(circle at top left, rgba(200, 166, 106, 0.24), transparent 38%),
    rgba(0, 0, 0, 0.26);
}

.ticket-header,
.history-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.ticket-list {
  display: grid;
  gap: 10px;
  margin-top: 18px;
}

.ticket-line {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 14px;
  align-items: center;
  padding: 14px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.07);
}

.ticket-line h3 {
  margin: 0;
  font-size: 17px;
}

.ticket-line p {
  margin: 5px 0 0;
  color: #aaa09b;
  font-size: 14px;
}

.ticket-line-price {
  display: grid;
  text-align: right;
  gap: 4px;
}

.ticket-line-price strong {
  font-size: 18px;
}

.ticket-line-price span {
  color: #aaa09b;
  font-size: 13px;
  font-weight: 800;
}

.line-actions {
  display: flex;
  gap: 8px;
}

.line-actions button {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  background: rgba(255, 255, 255, 0.07);
  color: #fff8f1;
  font-weight: 900;
  cursor: pointer;
}

.empty-ticket,
.empty-history {
  margin-top: 18px;
  padding: 24px;
  border-radius: 24px;
  text-align: center;
  color: #cfc2bd;
  background: rgba(0, 0, 0, 0.22);
  border: 1px dashed rgba(255, 255, 255, 0.15);
}

.empty-ticket h3 {
  margin: 0 0 8px;
  color: #fff8f1;
}

.ticket-note {
  display: grid;
  gap: 8px;
  margin-top: 18px;
}

.checkout-actions {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-top: 18px;
}

.history-panel {
  margin-top: 18px;
  padding: 24px;
}

.sales-history-list {
  display: grid;
  gap: 10px;
  margin-top: 18px;
}

.history-card {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.23);
  border: 1px solid rgba(255, 255, 255, 0.07);
}

.history-card h3 {
  margin: 0;
  font-size: 24px;
  letter-spacing: -0.04em;
}

.history-card span {
  display: block;
  color: #c8a66a;
  font-size: 13px;
  font-weight: 900;
  text-align: right;
}

.history-card strong {
  color: #cfc2bd;
  font-size: 13px;
}

.sales-shell input,
.sales-shell select {
  min-height: 52px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  outline: 0;
  background: rgba(6, 4, 6, 0.58);
  color: #fff8f1;
  padding: 0 14px;
  font-size: 15px;
  font-family: inherit;
}

.sales-shell input::placeholder {
  color: #766c68;
}

.sales-shell input:focus,
.sales-shell select:focus {
  border-color: rgba(200, 166, 106, 0.65);
  box-shadow: 0 0 0 3px rgba(200, 166, 106, 0.14);
}

.primary-action,
.ghost-action,
.cancel-action,
.cash-action,
.square-action,
.danger-action {
  min-height: 50px;
  padding: 0 18px;
  border-radius: 999px;
  border: 0;
  color: #fff8f1;
  font-weight: 900;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.14s ease, box-shadow 0.14s ease, background 0.14s ease;
}

.primary-action {
  background: linear-gradient(135deg, #8f123f, #c24a2f);
  box-shadow: 0 14px 32px rgba(194, 74, 47, 0.3);
}

.ghost-action,
.cancel-action {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.11);
}

.cash-action {
  background: rgba(47, 170, 118, 0.24);
  border: 1px solid rgba(118, 224, 170, 0.28);
  color: #9df0bd;
}

.square-action {
  background: rgba(43, 92, 192, 0.34);
  border: 1px solid rgba(128, 166, 255, 0.28);
  color: #cddaff;
}

.danger-action {
  background: rgba(155, 45, 45, 0.38);
  border: 1px solid rgba(255, 118, 118, 0.25);
  color: #ffb4a8;
}

.danger-action.small,
.cancel-action.small,
.cash-action.small,
.square-action.small,
.primary-action.small {
  min-height: 42px;
}

.primary-action:hover,
.ghost-action:hover,
.cancel-action:hover,
.cash-action:hover,
.square-action:hover,
.danger-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.24);
}

.primary-action:disabled,
.ghost-action:disabled,
.cancel-action:disabled,
.cash-action:disabled,
.square-action:disabled {
  opacity: 0.42;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* ---------- Modals ---------- */

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 26px;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.34), transparent 36%),
    rgba(5, 3, 5, 0.78);
  backdrop-filter: blur(12px);
}

.sales-modal {
  width: min(560px, 94vw);
  max-height: calc(100vh - 52px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 30px;
  background:
    linear-gradient(145deg, rgba(35, 22, 29, 0.98), rgba(14, 10, 14, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.11);
  color: #f7efe8;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.55);
}

.modal-topline {
  flex: 0 0 auto;
  height: 5px;
  background: linear-gradient(90deg, #8f123f, #c24a2f, #c8a66a);
}

.modal-topline.amber {
  background: linear-gradient(90deg, #c8a66a, #d77a2d);
}

.modal-topline.red {
  background: linear-gradient(90deg, #9b2d2d, #ff765f);
}

.modal-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 26px 28px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-header h2 {
  margin: 0;
  font-size: 36px;
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.modal-header p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.45;
}

.square-confirm-card {
  display: grid;
  gap: 8px;
  padding: 18px;
  border-radius: 22px;
  background: rgba(0, 0, 0, 0.24);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.square-confirm-card span {
  color: #b9aaa4;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.square-confirm-card strong {
  color: #f1c979;
  font-size: 34px;
  line-height: 1;
  letter-spacing: -0.06em;
}

.square-confirm-card p {
  margin: 0;
  color: #cfc2bd;
  line-height: 1.45;
}

.square-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.icon-close {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.07);
  color: #fff8f1;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 28px 28px;
}

/* Sticky modal footer/actions */
.modal-actions {
  position: sticky;
  bottom: 0;
  z-index: 3;
  margin: 18px -28px -28px;
  padding: 18px 28px 24px;
  background:
    linear-gradient(
      180deg,
      rgba(14, 10, 14, 0),
      rgba(14, 10, 14, 0.94) 30%,
      rgba(14, 10, 14, 1)
    );
}

/* Nicer modal scrollbar */
.modal-body::-webkit-scrollbar {
  width: 10px;
}

.modal-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(200, 166, 106, 0.45);
  border-radius: 999px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: rgba(200, 166, 106, 0.65);
}

.delete-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

@media (max-width: 760px) {
  .modal-overlay {
    align-items: stretch;
    padding: 12px;
  }

  .sales-modal {
    width: 100%;
    max-height: calc(100vh - 24px);
  }

  .modal-header {
    padding: 22px 20px 16px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-actions {
    margin: 18px -20px -20px;
    padding: 18px 20px 22px;
  }
}



.open-tables-panel {
  margin-top: 18px;
  padding: 24px;
}

.open-tables-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.open-tables-header h2 {
  margin: 0;
  font-size: clamp(28px, 4vw, 44px);
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.open-table-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.open-table-card {
  min-width: 150px;
  padding: 14px 16px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.24);
  color: #fff8f1;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
}

.open-table-card strong,
.open-table-card span,
.open-table-card b {
  display: block;
}

.open-table-card strong {
  font-size: 18px;
}

.open-table-card span {
  margin-top: 5px;
  color: #cfc2bd;
  font-size: 13px;
}

.open-table-card b {
  margin-top: 8px;
  color: #f1c979;
  font-size: 20px;
}

.open-table-card.active {
  border-color: rgba(241, 201, 121, 0.55);
  box-shadow: 0 0 0 2px rgba(241, 201, 121, 0.12);
}

.empty-open-tables {
  padding: 18px;
  border-radius: 20px;
  color: #cfc2bd;
  background: rgba(0, 0, 0, 0.22);
  border: 1px dashed rgba(255, 255, 255, 0.15);
}

.empty-open-tables p {
  margin: 0;
}

.active-table-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
}

.active-table-banner span {
  margin-right: auto;
  color: #cfc2bd;
}

.active-table-banner strong {
  color: #fff8f1;
}

.cash-tender-card {
  display: grid;
  gap: 16px;
  padding: 18px;
  border-radius: 22px;
  background: rgba(0, 0, 0, 0.24);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.cash-tender-card div,
.cash-tender-card label {
  display: grid;
  gap: 8px;
}

.cash-tender-card span {
  color: #b9aaa4;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.cash-tender-card strong {
  color: #f1c979;
  font-size: 34px;
  line-height: 1;
  letter-spacing: -0.06em;
}

.cash-tender-card strong.bad-change {
  color: #ffb4a8;
}

.cash-tender-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* ---------- Responsive ---------- */

@media (max-width: 1250px) {
  .pour-grid {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

  .register-grid {
    grid-template-columns: 1fr;
  }

  .night-prices {
    grid-template-columns: 1fr;
  }
}

.bottle-panel {
  margin-top: 18px;
  padding: 18px;
  border-radius: 24px;
  background:
    radial-gradient(circle at top left, rgba(200, 166, 106, 0.16), transparent 38%),
    rgba(0, 0, 0, 0.24);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.bottle-panel-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.bottle-panel-header h3 {
  margin: 0;
  font-size: 24px;
  letter-spacing: -0.05em;
}

.bottle-entry-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 10px;
  margin-top: 10px;
}

.bottle-add-button {
  width: 100%;
  margin-top: 12px;
}

.ghost-action.small {
  min-height: 40px;
  padding: 0 14px;
  font-size: 13px;
}

.reserve-form {
  display: grid;
  gap: 14px;
}

.modal-topline.gold {
  background: linear-gradient(90deg, #c8a66a, #f1c979);
}

.history-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.adjustment-modal {
  width: min(880px, 94vw);
}

.adjustment-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 16px;
}

.adjustment-summary div,
.adjustment-helper,
.adjustment-history-card {
  padding: 16px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.075);
}

.adjustment-summary span,
.adjustment-history-money span {
  display: block;
  color: #9b8f8b;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.adjustment-summary strong {
  display: block;
  margin-top: 6px;
  color: #fff8f1;
  font-size: 20px;
  letter-spacing: -0.03em;
}

.form-panel {
  padding: 18px;
  border-radius: 24px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.075);
}

.panel-heading {
  margin-bottom: 16px;
}

.panel-heading h3 {
  margin: 0;
  font-size: 24px;
  letter-spacing: -0.04em;
}

.panel-heading p {
  margin: 6px 0 0;
  color: #a99d98;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 14px;
}

.form-grid .full-width {
  grid-column: 1 / -1;
}

.adjustment-form label {
  display: grid;
  gap: 8px;
}

.adjustment-form label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.adjustment-helper {
  margin-top: 16px;
}

.adjustment-helper h3 {
  margin: 0;
  color: #f1c979;
  font-size: 22px;
  letter-spacing: -0.03em;
}

.adjustment-helper p {
  margin: 8px 0 0;
  color: #cfc2bd;
  line-height: 1.5;
}

.adjustment-history-list {
  display: grid;
  gap: 12px;
}

.adjustment-history-card {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 14px;
  align-items: center;
}

.adjustment-history-card h3 {
  margin: 0;
  font-size: 20px;
  letter-spacing: -0.03em;
}

.adjustment-history-card p {
  margin: 6px 0 0;
  color: #cfc2bd;
}

.adjustment-history-money {
  text-align: right;
}

.adjustment-history-money strong {
  display: block;
  color: #f1c979;
  font-size: 22px;
  letter-spacing: -0.04em;
}

.modal-topline.gold {
  background: linear-gradient(90deg, #c8a66a, #f1c979);
}

.history-card.adjusted {
  border-color: rgba(200, 166, 106, 0.34);
  background:
    radial-gradient(circle at top right, rgba(200, 166, 106, 0.13), transparent 34%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.035));
}

.history-sale-main {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.history-title-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.sale-total-stack {
  display: grid;
  gap: 3px;
  text-align: right;
  min-width: 110px;
}

.sale-total-stack span {
  color: #9b8f8b;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.sale-total-stack strong {
  color: #fff8f1;
  font-size: 18px;
  letter-spacing: -0.03em;
}

.sale-total-stack .refund-text {
  color: #ffb4a8;
}

.sale-total-stack .net-sale-text {
  color: #f1c979;
  font-size: 22px;
}

.adjusted-badge {
  display: inline-flex;
  min-height: 30px;
  align-items: center;
  padding: 0 10px;
  border-radius: 999px;
  color: #f1c979;
  font-size: 12px;
  font-weight: 900;
  background: rgba(200, 166, 106, 0.16);
  border: 1px solid rgba(200, 166, 106, 0.28);
}

.history-meta {
  display: grid;
  gap: 4px;
  color: #cfc2bd;
}

.history-meta span {
  color: #9b8f8b;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.history-meta strong {
  color: #fff8f1;
}

.adjustment-card-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.adjustment-card-summary span {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  color: #cfc2bd;
  font-size: 12px;
  font-weight: 800;
  background: rgba(0, 0, 0, 0.24);
  border: 1px solid rgba(255, 255, 255, 0.075);
}

.adjustment-card-summary strong {
  color: #f1c979;
  margin-left: 4px;
}

.history-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 12px;
}

.has-adjustments-button {
  color: #f1c979 !important;
  border-color: rgba(200, 166, 106, 0.36) !important;
  background: rgba(200, 166, 106, 0.12) !important;
}

.field-note {
  grid-column: 1 / -1;
  margin: -4px 0 0;
  color: #cfc2bd;
  font-size: 13px;
  line-height: 1.4;
}

.plate-template-list {
  display: grid;
  gap: 12px;
  margin-top: 16px;
}

.plate-template-card {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 16px;
  border-radius: 20px;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.075);
  transition: transform 0.14s ease, border-color 0.14s ease, background 0.14s ease;
}

.plate-template-card:hover {
  transform: translateY(-1px);
  border-color: rgba(200, 166, 106, 0.28);
}

.plate-template-card.selected {
  background: rgba(200, 166, 106, 0.13);
  border-color: rgba(200, 166, 106, 0.42);
}

.plate-template-card h3 {
  margin: 0;
  font-size: 24px;
  letter-spacing: -0.04em;
}

.plate-template-card p {
  margin: 6px 0 0;
  color: #cfc2bd;
  line-height: 1.4;
}

.template-check {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  color: #171016;
  background: #f1c979;
  font-size: 22px;
  font-weight: 900;
}

.plate-menu-button {
  min-width: 150px;
}

.food-menu-accordion {
  display: grid;
  gap: 12px;
}

.food-menu-item {
  overflow: hidden;
  border-radius: 24px;
  background:
    radial-gradient(circle at top left, rgba(200, 166, 106, 0.1), transparent 42%),
    rgba(0, 0, 0, 0.24);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: border-color 0.14s ease, background 0.14s ease, box-shadow 0.14s ease;
}

.food-menu-item.open {
  border-color: rgba(200, 166, 106, 0.38);
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.28);
}

.food-menu-summary {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 18px;
  align-items: center;
  padding: 18px;
  border: 0;
  color: #fff8f1;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
}

.food-menu-summary:hover {
  background: rgba(255, 255, 255, 0.035);
}

.food-menu-title h3 {
  margin: 0;
  font-size: 26px;
  line-height: 1.05;
  letter-spacing: -0.045em;
}

.food-menu-title p:last-child {
  margin: 8px 0 0;
  color: #cfc2bd;
  line-height: 1.4;
}

.food-menu-summary-right {
  display: grid;
  grid-template-columns: auto 42px;
  gap: 12px;
  align-items: center;
}

.food-menu-summary-right strong {
  color: #f1c979;
  font-size: 24px;
  letter-spacing: -0.04em;
  white-space: nowrap;
}

.food-menu-summary-right span {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  color: #171016;
  background: #f1c979;
  font-size: 28px;
  font-weight: 900;
  line-height: 1;
}

.food-menu-details {
  display: grid;
  gap: 16px;
  padding: 0 18px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.food-menu-details .plate-setup-header {
  padding-top: 18px;
}

.plate-menu-modal {
  width: min(860px, 94vw);
}

.plate-options-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(260px, 1fr));
  gap: 14px;
}

.plate-option-card,
.plate-template-card {
  padding: 18px;
  border-radius: 22px;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.075);
  transition: transform 0.14s ease, border-color 0.14s ease, background 0.14s ease;
}

.plate-option-card:hover,
.plate-template-card:hover {
  transform: translateY(-1px);
  border-color: rgba(200, 166, 106, 0.28);
}

.plate-option-card.selected,
.plate-template-card.selected {
  background: rgba(200, 166, 106, 0.13);
  border-color: rgba(200, 166, 106, 0.42);
}

.plate-option-top {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.plate-option-top h3,
.plate-template-card h3 {
  margin: 0;
  font-size: 26px;
  letter-spacing: -0.04em;
}

.plate-option-top strong {
  color: #f1c979;
  font-size: 24px;
  letter-spacing: -0.04em;
}

.plate-description,
.plate-template-card p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.4;
}

.plate-ingredients {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.plate-ingredients span {
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  padding: 0 10px;
  border-radius: 999px;
  color: #cfc2bd;
  font-size: 12px;
  font-weight: 800;
  background: rgba(255, 255, 255, 0.07);
}

.plate-cost-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
}

.plate-cost-row span {
  color: #9b8f8b;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.plate-cost-row strong {
  color: #f1c979;
}

.plate-add-panel {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 14px;
  align-items: end;
  margin-top: 18px;
  padding: 16px;
  border-radius: 22px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.075);
}

.plate-add-panel label {
  display: grid;
  gap: 8px;
}

.plate-add-panel label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.plate-template-list {
  display: grid;
  gap: 12px;
  margin-top: 16px;
}

.plate-template-card {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: center;
}

.template-check {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  color: #171016;
  background: #f1c979;
  font-size: 22px;
  font-weight: 900;
}

@media (max-width: 760px) {
  .plate-options-grid,
  .plate-add-panel {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .history-sale-main {
    flex-direction: column;
  }

  .sale-total-stack {
    text-align: left;
  }

  .history-actions {
    flex-direction: column;
    align-items: stretch;
  }
}

@media (max-width: 760px) {
  .history-actions,
  .adjustment-history-card {
    flex-direction: column;
    grid-template-columns: 1fr;
    align-items: stretch;
  }

  .adjustment-summary,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .adjustment-history-money {
    text-align: left;
  }
}

@media (max-width: 720px) {
  .bottle-panel-header,
  .bottle-entry-row {
    grid-template-columns: 1fr;
    flex-direction: column;
  }
}

@media (max-width: 720px) {
  .sales-shell {
    padding: 16px;
  }

  .sales-hero,
  .ticket-header,
  .history-header,
  .open-tables-header {
    flex-direction: column;
    align-items: stretch;
  }

  .sales-hero {
    padding: 24px;
  }

  .pour-grid {
    grid-template-columns: 1fr;
  }

  .checkout-actions,
  .history-card,
  .ticket-line {
    grid-template-columns: 1fr;
  }

  .ticket-line-price,
  .history-card span {
    text-align: left;
  }

  .line-actions {
    justify-content: flex-start;
  }

  .primary-action,
  .ghost-action,
  .cancel-action,
  .cash-action,
  .square-action,
  .danger-action {
    width: 100%;
  }

  .modal-overlay {
    padding: 12px;
    align-items: stretch;
  }

  .sales-modal {
    width: 100%;
    max-height: calc(100vh - 24px);
    border-radius: 24px;
  }

  .modal-header {
    flex-direction: column;
  }

  .delete-actions {
    flex-direction: column;
  }
}

@media (max-width: 720px) {
  .food-menu-summary {
    grid-template-columns: 1fr;
  }

  .food-menu-summary-right {
    grid-template-columns: 1fr auto;
  }

  .food-menu-summary-right strong {
    text-align: left;
  }
}

.retail-button {
  border: 1px solid rgba(40, 95, 80, 0.25);
}

.half-glass-button {
  border: 1px solid rgba(200, 166, 106, 0.22);
}

.misc-sale-button {
  border: 1px solid rgba(140, 140, 180, 0.28);
}

.wine-tour-button {
  border: 1px solid rgba(120, 53, 15, 0.25);
}

.wine-tour-modal {
  width: min(720px, calc(100vw - 32px));
  max-height: calc(100vh - 48px);
  overflow-y: auto;
}

.wine-tour-form {
  margin-bottom: 18px;
}

.save-default-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  color: #f7efe8;
  font-weight: 700;
}

.save-default-row input {
  width: auto;
}

.wine-tour-summary-card {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin: 16px 0;
  padding: 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.wine-tour-summary-card div {
  display: grid;
  gap: 4px;
}

.wine-tour-summary-card span {
  color: rgba(247, 239, 232, 0.68);
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.wine-tour-summary-card strong {
  font-size: 1.15rem;
}

.wine-tour-component-preview {
  display: grid;
  gap: 6px;
  margin-bottom: 18px;
  padding: 14px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.16);
  color: rgba(247, 239, 232, 0.88);
}


.wine-tour-locked-plate {
  display: grid;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(90, 66, 48, 0.18);
  background: rgba(255, 255, 255, 0.12);
}

.wine-tour-locked-plate span {
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(247, 239, 232, 0.72);
}

.wine-tour-locked-plate strong {
  font-size: 1.05rem;
  color: #fff7ed;
}

.wine-tour-locked-plate p {
  margin: 0;
  color: rgba(247, 239, 232, 0.72);
  font-size: 0.9rem;
}

@media (max-width: 700px) {
  .wine-tour-modal {
    width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
    padding: 16px;
    border-radius: 14px;
  }

  .wine-tour-summary-card {
    grid-template-columns: 1fr;
  }

  .wine-tour-modal input,
  .wine-tour-modal select {
    font-size: 16px;
  }
}

</style>